import { Hinter, Hint } from "./hinter";

const { SUDOKU_STRATEGIES } = require("../utils/sudokuStrategies");

export type SudokuUnitType = "row" | "column" | "box";

export interface CompletedUnitPayload {
    type: SudokuUnitType;
    index: number;
    cellIds: number[];
}

export interface UnitsCompletedEvent {
    originCellId: number;
    units: CompletedUnitPayload[];
}

export interface SolverStep {
    strategy: string;
    description: string;
    strategyKey?: string;
    boardState: string;
    cellId?: number;
}

export interface SolverRunResult {
    success: boolean;
    usedBruteForce: boolean;
    steps: SolverStep[];
}

let UI_UPDATE_CHUNK_SIZE = 32;

/**
 * Full Sudoku Board including Settings about game play.
 */
export class SudokuGame {
    /**
    * Render the answer cells into the board when this flag is set
    * @type {boolean}
    */
    showSolution = false;

    /**
     * When true incorrect moves will be displayed in red
     * @type {boolean}
     */
    showIncorrectMoves = true;

    /**
     * If true, when a guess is added to a cell, that number will be removed from the possibilities in the shared rows, columns and boxes.
     * @type {boolean}
     */
    automaticallyRemovePossibilities = false;

    /**
     * Shows the possibilities in grid format
     * @type {boolean}
     */
    possibilitiesInGrid = false;

    /**
     * If true, highlight each number in the cell based on the active number
     * @type {boolean}
     */
    highlightActiveNumber: "false" | "true" | "highlight" = "true";

    /**
     * The number that is currently active for the user
     * @type {string}
     */
    activeNumber: string | null = null;

    /**
     * The current active cell
     * @type {Cell}
     */
    activeCell: Cell | null = null;

    /**
     * If true, highlight the active cell
     * @type {boolean}
     */
    highlightActiveCell = false;

    /**
     * If true, highlight all cells in the same row, column, and box as the active cell
     * @type {boolean}
     */
    highlightRelatedCells = true;

    /**
     * If true, show candidate popup on hover over the active empty cell (desktop only)
     * @type {boolean}
     */
    showCandidatePopup = true;

    /**
     * If true, we're in solver/enter-your-own-puzzle mode where there's no known solution
     * @type {boolean}
     */
    isSolverMode = false;

    /**
     * If true, the board has been locked and current guesses have been set as given values
     * @type {boolean}
     */
    isLocked = false;

    /**
     * Stores the user's choice for using their possibilities in hints.
     */
    userPossibilitiesChoice: boolean | null = null;

    /**
     * Tracks the board state used to generate the cached solver solution.
     */
    private solverSolutionSignature: string | null = null;

    /**
     * The game cells
     * @type {Cell[]}
     */
    cells: Cell[] = [];

    /**
     * Tracks completed row indices
     */
    private completedRows: Set<number> = new Set();

    /**
     * Tracks completed column indices
     */
    private completedColumns: Set<number> = new Set();

    /**
     * Tracks completed box indices
     */
    private completedBoxes: Set<number> = new Set();

    /** Queue of cells awaiting DOM updates. */
    private pendingCellUpdates: number[] = [];

    /** Tracks which cell IDs are already queued. */
    private pendingCellSet: Set<number> = new Set();

    /** Prevents scheduling multiple animation-frame flushes. */
    private isFlushScheduled = false;

    /** Cached list of all cell ids for full-board renders. */
    private allCellIds: number[] = [];

    /** Cached box dimensions for quick lookups. */
    private readonly boxColumnCount: number;
    private readonly boxRowCount: number;

    /** Cached DOM elements keyed by cell id. */
    private cellElements: (HTMLTableCellElement | undefined)[] = [];

    /** Snapshot of the last render state for targeted updates. */
    private lastRenderState: RenderStateSnapshot | null = null;

    /**
     * Callback fired when units finish
     */
    onUnitsCompleted?: (payload: UnitsCompletedEvent) => void;
    cages: CageDefinition[] = [];
    private cagesByCell: Map<number, CageDefinition> = new Map();

    /**
     * Tracks the most recent solver step history.
     */
    private solverHistory: SolverStep[] = [];

    /**
     * Indicates whether brute force was required in the last solver run.
     */
    private solverUsedBruteForce = false;

    /**
     * Guards solver tracking so we only record during active runs.
     */
    private solverRunActive = false;

    /**
     * Optional list of strategy keys to restrict hint/solver attempts.
     */
    allowedStrategyKeys: Set<string> | null = null;

    /**
     * The max undos allowed
     */
    readonly MAX_UNDOS = 200;

    /**
     * The set of valid inputs
     * @type {Set<string>}
     */
    validInputs: Set<string> = new Set();

    /**
     * The size of the game (example size = 9 would mean a 9x9 board)
     * @type {number}
     */
    size: number;

    /**
     * The undos for the game
     * @type {string[]}
     */
    undos: string[] = [];

    /**
     * List of strategies that can be used to provide hints
     */
    strategies: Record<string, { name: string; link: string; practice?: string }> = SUDOKU_STRATEGIES;

    /**
     *
     * @param {number} size - The size of the game
     */
    constructor(size: number, solution?: string[] | number[], startingPosition?: string[] | number[]) {
        this.size = size;
        // create an array the game cells
        this.boxColumnCount = this.computeBoxColumnCount();
        this.boxRowCount = this.computeBoxRowCount();

        for (let i = 0; i < size * size; i++) {
            const cell = new Cell();
            const row = Math.floor(i / size)
            const col = i % size;
            const boxWidth = this.boxColumnCount;
            const boxHeight = this.boxRowCount;
            const boxRow = Math.floor(row / boxHeight);
            const boxColumn = Math.floor(col / boxWidth);
            cell.id = i;
            cell.row = row;
            cell.column = col;
            cell.box = boxRow * (size / boxWidth) + boxColumn

            this.cells.push(cell);
            this.allCellIds.push(i);
            const element = document.getElementById(`T${i}`) as HTMLTableCellElement | null;
            this.cellElements.push(element ?? undefined);
        }

        const possibilites = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F', 'G'];
        for (var i = 0; i < size; i++) {
            this.validInputs.add(possibilites[i])
        }

        // Set cell values
        if (solution && startingPosition) {
            this.cells.forEach((cell, idx) => {
                const solValue = solution[idx].toString();
                const startVal = startingPosition[idx].toString();
                const isPossibilities = startVal.indexOf('P') === 0;
                const isGiven = startVal != '0' && !isPossibilities && startVal.trim() != '';

                cell.answer = solValue;

                if (isPossibilities) {
                    const possibilities = startVal.substring(1).split('');
                    cell.possibilities = new Set(possibilities.filter(p => this.validInputs.has(p)));
                    cell.solverPossibilites = new Set(cell.possibilities);
                } else if (isGiven) {
                    cell.guess = solValue;  // Use solValue in case startingPosition has errors
                    cell.isGiven = true;
                }
            })
        }

        this.recalcAllUnitCompletions(false);
        this.resetSolverTracking();
    }

    /**
     * Reload an existing game instance with a new puzzle.
     * Used by Infinite mode to swap boards without rebuilding DOM/state.
     */
    loadPuzzle(solution: (string | number)[], startingPosition: (string | number)[]) {
        if (!solution || !startingPosition || solution.length !== this.cells.length || startingPosition.length !== this.cells.length) {
            return;
        }

        this.completedRows.clear();
        this.completedColumns.clear();
        this.completedBoxes.clear();
        this.validInputs.clear();
        this.pendingCellUpdates = [];
        this.pendingCellSet.clear();
        this.resetSolverTracking();
        this.isSolverMode = false;
        this.isLocked = false;

        const possibilites = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F', 'G'];
        for (let i = 0; i < this.size; i++) {
            this.validInputs.add(possibilites[i]);
        }

        this.cells.forEach((cell, idx) => {
            const solValue = solution[idx].toString();
            const startVal = startingPosition[idx].toString();
            const isPossibilities = startVal.indexOf('P') === 0;
            const isGiven = startVal != '0' && !isPossibilities && startVal.trim() != '';

            cell.answer = solValue;
            cell.guess = null;
            cell.isGiven = false;
            cell.possibilities.clear();
            cell.solverPossibilites.clear();

            if (isPossibilities) {
                const possibilities = startVal.substring(1).split('');
                cell.possibilities = new Set(possibilities.filter(p => this.validInputs.has(p)));
                cell.solverPossibilites = new Set(cell.possibilities);
            } else if (isGiven) {
                cell.guess = solValue;  // Use solValue in case startingPosition has errors
                cell.isGiven = true;
            }
            cell.markDirty();
        });

        this.recalcAllUnitCompletions(false);
        this.updateGameBoardUI();
    }

    private resetSolverTracking() {
        this.solverHistory = [];
        this.solverUsedBruteForce = false;
        this.solverRunActive = false;
    }

    private startSolverRun() {
        this.resetSolverTracking();
        this.solverRunActive = true;
    }

    private completeSolverRun(usedBruteForce: boolean) {
        this.solverUsedBruteForce = usedBruteForce;
        this.solverRunActive = false;
    }

    private recordSolverStep(strategyKeyOrName: string | undefined, descriptions: string[], cellId?: number) {
        if (!this.solverRunActive) {
            return;
        }

        const strategyLabel = this.formatStrategyLabel(strategyKeyOrName);
        const sanitizedDescriptions = descriptions
            .map(desc => this.sanitizeSolverText(desc))
            .filter(desc => desc.length > 0);

        const finalDescriptions = sanitizedDescriptions.length
            ? sanitizedDescriptions
            : [`Applied ${strategyLabel}`];

        const boardState = this.serialize();

        this.solverHistory.push({
            strategy: strategyLabel,
            description: finalDescriptions.join(". "),
            strategyKey: strategyKeyOrName ?? undefined,
            boardState,
            cellId,
        });
    }

    private formatStrategyLabel(strategyKeyOrName: string | undefined): string {
        if (!strategyKeyOrName) {
            return "Unknown Strategy";
        }

        const strategy = this.strategies[strategyKeyOrName];
        if (strategy?.name) {
            return strategy.name;
        }

        return strategyKeyOrName;
    }

    private sanitizeSolverText(text: string): string {
        if (!text) {
            return "";
        }
        return text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    }

    getSolverHistory(): SolverStep[] {
        return this.solverHistory.map(step => ({ ...step }));
    }

    didUseBruteForce(): boolean {
        return this.solverUsedBruteForce;
    }

    setCages(cages: CageDefinition[]) {
        this.cages = cages;
        this.cagesByCell.clear();
        cages.forEach((cage) => {
            cage.cells.forEach((cellId) => {
                this.cagesByCell.set(cellId, cage);
            });
        });
    }

    getCageForCell(cellId: number): CageDefinition | undefined {
        return this.cagesByCell.get(cellId);
    }

    getCageBordersForCell(cellId: number) {
        const cage = this.getCageForCell(cellId);
        if (!cage) {
            return {
                borders: { top: false, right: false, bottom: false, left: false },
                corners: { topLeft: false, topRight: false, bottomRight: false, bottomLeft: false },
            };
        }

        const cell = this.getCell(cellId);
        if (!cell) {
            return {
                borders: { top: false, right: false, bottom: false, left: false },
                corners: { topLeft: false, topRight: false, bottomRight: false, bottomLeft: false },
            };
        }

        const neighbors = {
            top: cellId - this.size,
            right: cell.column < this.size - 1 ? cellId + 1 : -1,
            bottom: cellId + this.size,
            left: cell.column > 0 ? cellId - 1 : -1,
        };

        const borders = {
            top: neighbors.top < 0 || this.getCageForCell(neighbors.top)?.id !== cage.id,
            right: neighbors.right < 0 || this.getCageForCell(neighbors.right)?.id !== cage.id,
            bottom: neighbors.bottom >= this.cells.length || this.getCageForCell(neighbors.bottom)?.id !== cage.id,
            left: neighbors.left < 0 || this.getCageForCell(neighbors.left)?.id !== cage.id,
        };

        // Helper to get borders for a neighbor cell (no recursion)
        const calcNeighborBorders = (neighborIdx: number, cageId: number) => {
            if (neighborIdx < 0 || neighborIdx >= this.cells.length) return null;
            const neighborCell = this.getCell(neighborIdx);
            if (!neighborCell) return null;
            const neighborCage = this.getCageForCell(neighborIdx);
            if (!neighborCage) return null;
            return {
                top: neighborIdx - this.size < 0 || this.getCageForCell(neighborIdx - this.size)?.id !== neighborCage.id,
                right: neighborCell.column < this.size - 1 ? this.getCageForCell(neighborIdx + 1)?.id !== neighborCage.id : true,
                bottom: neighborIdx + this.size >= this.cells.length || this.getCageForCell(neighborIdx + this.size)?.id !== neighborCage.id,
                left: neighborCell.column > 0 ? this.getCageForCell(neighborIdx - 1)?.id !== neighborCage.id : true,
            };
        };

        // Calculate corners based on neighbor cell borders
        let corners = {
            topLeft: false,
            topRight: false,
            bottomRight: false,
            bottomLeft: false,
        };

        // Top Right Corner
        const topBorders = calcNeighborBorders(neighbors.top, cage.id);
        const rightBorders = calcNeighborBorders(neighbors.right, cage.id);
        if (topBorders && rightBorders) {
            if (topBorders.right && !topBorders.bottom && rightBorders.top && !rightBorders.left) {
                corners.topRight = true;
            }
        }

        // Top Left Corner
        const leftBorders = calcNeighborBorders(neighbors.left, cage.id);
        if (topBorders && leftBorders) {
            if (topBorders.left && !topBorders.bottom && leftBorders.top && !leftBorders.right) {
                corners.topLeft = true;
            }
        }

        // Bottom Left Corner
        const bottomBorders = calcNeighborBorders(neighbors.bottom, cage.id);
        if (leftBorders && bottomBorders) {
            if (leftBorders.bottom && !leftBorders.right && bottomBorders.left && !bottomBorders.top) {
                corners.bottomLeft = true;
            }
        }

        // Bottom Right Corner
        if (rightBorders && bottomBorders) {
            if (rightBorders.bottom && !rightBorders.left && bottomBorders.right && !bottomBorders.top) {
                corners.bottomRight = true;
            }
        }

        return { borders, corners };
    }

    /**
     * Generates a string representation of the game state
     * Serialized format: ?s=0|0|P123|1|2|0|0|0|3|1:1|2|3|1|2|4|1|2|3|1
     */
    getGameString() {
        return `${this.serialize()}:${this.cells.map(c => c.answer).join('|')}`;
    }

    /**
     * Returns the current board as an 81-character string (0 = empty).
     */
    getUnsolvedString(): string {
        return this.cells.map((cell) => {
            const rawValue = cell.guess ?? (cell.isGiven ? cell.answer : null);
            const trimmed = rawValue?.toString().trim() ?? "";
            if (!trimmed || trimmed === "0") {
                return "0";
            }
            if (!this.isValidInput(trimmed)) {
                return "0";
            }
            return trimmed;
        }).join("");
    }

    /**
     * Returns the solved board as an 81-character string (0 = unknown).
     */
    getSolvedString(): string {
        return this.cells.map((cell) => {
            const value = cell.answer?.toString().trim() ?? "";
            if (!value || value === "0") {
                return "0";
            }
            if (!this.isValidInput(value)) {
                return "0";
            }
            return value;
        }).join("");
    }

    /**
     * Save the current state of the game
     */
    saveUndo() {
        // Check if the current state of the game is the same as the last undo
        var newUndo = this.serialize();
        const previousUndo = this.undos[this.undos.length - 1];
        if (newUndo == previousUndo) {
            return;
        }
        this.undos.push(newUndo)
    }

    /** Set the active cell */
    setActiveCell(cellId) {
        const previousCell = this.activeCell;
        const cell = this.cells[cellId];
        this.activeCell = cell;
        if (previousCell && previousCell !== cell) {
            previousCell.markDirty();
        }
        if (cell && previousCell !== cell) {
            cell.markDirty();
        }
    }

    /** Clear the active cell and number so no related unit remains highlighted. */
    clearActiveSelection(): boolean {
        const hadActiveSelection = Boolean(this.activeCell || this.activeNumber);
        if (this.activeCell) {
            this.activeCell.markDirty();
        }
        this.activeCell = null;
        this.activeNumber = null;
        return hadActiveSelection;
    }

    /**
     * Change the active cell to the one in the direction specified
     * @param {string} direction - "up", "down", "left", "right"
     */
    moveActiveCell(direction) {

        // If no active cell, just return
        if (!this.activeCell) {
            return;
        }

        const previousCell = this.activeCell;
        var row = previousCell.row;
        var col = previousCell.column;

        if (direction == "up") {
            row--;
        } else if (direction == "down") {
            row++;
        } else if (direction == "left") {
            col--;
        } else if (direction == "right") {
            col++;
        }
        // Check if the new cell is out of bounds
        if (row < 0 || row >= this.size || col < 0 || col >= this.size) {
            return;
        }
        var newCell = this.cells[row * this.size + col];
        this.activeCell = newCell;
        if (previousCell && previousCell !== newCell) {
            previousCell.markDirty();
        }
        if (newCell && previousCell !== newCell) {
            newCell.markDirty();
        }
    }

    /**
     * Returns an array of numbers that have been completely used in the puzzle
     * @returns {string[]}
     */
    checkUsedNumbers() {
        const finished: string[] = [];
        this.cells.reduce((acc, cell) => {
            const guess = cell.guess;
            if (guess) {
                if (!acc[guess]) {
                    acc[guess] = 0;
                }
                acc[guess]++;
                // Check if we have used all the numbers
                if (acc[guess] == this.size) {
                    finished.push(guess)
                }
            }
            return acc
        }, {})
        return finished;
    }

    /**
     * Return true if all cells are filled in and correct
     */
    isComplete() {
        return this.cells.every(cell => cell.isCorrect())
    }

    /**
     * Lock the game by setting current guesses as given values.
     * This is typically used in solver/enter-your-own-puzzle mode to establish
     * the starting position after the user has entered their puzzle.
     */
    lockBoard() {
        for (let i = 0; i < this.cells.length; i++) {
            const cell = this.cells[i];
            const guess = cell.guess;
            if (guess !== null && guess !== undefined && guess !== '') {
                cell.answer = guess;
                cell.isGiven = true;
                cell.markDirty();
            }
        }
        this.isLocked = true;
        this.resetSolverTracking();
    }

    /**
     * Unlock the board by allowing edits to the cells and removing answer and given status
     * @returns {void}
     */
    unlockBoard() {
        this.isLocked = false;
        for (const cell of this.cells) {
            cell.isGiven = false;
            cell.answer = "";
            cell.markDirty();
        }
        this.resetSolverTracking();
    }

    getConflicts() {
        // Check for conflicts in the cell's row, column, and box
        for (const cell of this.cells) {
            if (cell.guess) {
                const conflictCells = this.getAllRelatedCells(cell.id).filter(c => c.id != cell.id && c.guess == cell.guess);
                if (conflictCells.length) {
                    return [cell, ...conflictCells];
                }
            }
        }
        return [];
    }

    highlightCells(cells: Cell[]) {
        cells.forEach((c) => {
            c.isDescription = true;
            c.markDirty();
        });
        this.updateGameBoardUI();
        this.runAfterPendingFlush(() => {
            cells.forEach((c) => {
                c.isDescription = false;
                c.markDirty();
            });
        });
    }

    /**
     * Returns true if any of the filled in cells is incorrect.
     */
    hasErrors() {
        // In solver mode, we don't have a known solution, so we can't check for errors
        if (this.isSolverMode) {
            return false;
        }
        return this.cells.some((cell) => cell.isWrong())
    }

    serialize() {
        return this.cells.map(cell => cell.serialize()).join('|')
    }

    /**
     * Checks if input is a valid character to enter in the game
     * @param {any} input
     */
    isValidInput(input) {
        return this.validInputs.has(input);
    }

    /**
     * Add a possibility to a cell, or remove it if the cell already contains the possibility.
     * @param {number} cellId The cell index
     * @param {string} possibility - the possibility
     */
    togglePossibility(cellId, possibility) {
        possibility = possibility.toString();
        // Dont allow invalid input
        if (!this.isValidInput(possibility)) {
            return;
        }

        const cell = this.cells[cellId];
        const removedGuess = !cell.isGiven && Boolean(cell.guess);
        // Clear a guess if this isn't a given cell
        if (!cell.isGiven) {
            cell.guess = null;
        }
        if (removedGuess) {
            this.calculateAllPossibilities();
        }
        cell.togglePossibility(possibility)
    }

    /**
     * Add a guess to a cell
     * @param {number} cellId
     * @param {string} guess
     * @returns
     */
    addGuess(cellId, guess) {
        // convert to a string so we don't have '1' and 1 in the list of possibilities
        guess = guess.toString();
        // Dont allow invalid input
        if (!this.isValidInput(guess)) {
            return;
        }
        const theCell = this.cells[cellId]

        // Don't edit given cells
        if (theCell.isGiven) {
            return;
        }

        // Don't allow the user to update the board during show solution
        if (this.showSolution) {
            return;
        }

        const previousGuess = theCell.guess;

        // if the user is adding the same value as is currently in guess,
        // then just remove the guess
        if (theCell.guess == guess) {
            theCell.guess = null;
            theCell.markDirty();
            // A removed constraint can make candidates valid again, so rebuild
            // instead of retaining eliminations from the previous board state.
            this.calculateAllPossibilities();
            this.evaluateUnitCompletion(cellId, false);
            return;
        }

        let result = theCell.addGuess(guess);

        if (previousGuess) {
            // Replacing a value removes one constraint and adds another. Rebuild
            // before applying the new placement so candidates do not depend on
            // the sequence of edits that produced the current board.
            this.calculateAllPossibilities();
        }

        if (this.automaticallyRemovePossibilities) {
            this.removeRelatedPossibilities(cellId, guess)
        }
        this.removeRelatedSolverPossibilities(cellId, guess)
        this.evaluateUnitCompletion(cellId, true);
        return result;
    }

    /**
     * Remove the possibility from all cells that share a row, column or box with the cell at cellId
     * @param {number} cellId - the cell index
     * @param {string} possibility
     */
    removeRelatedPossibilities(cellId, possibility) {
        const relatedCells = this.getAllRelatedCells(cellId)
        relatedCells.forEach((cell) => {
            cell.removePossibility(possibility);
        })
    }

    /**
     * Apply the basic Sudoku constraint from a placement to the solver's candidates.
     * @param {number} cellId - the placed cell index
     * @param {string} possibility - the placed value
     */
    removeRelatedSolverPossibilities(cellId, possibility) {
        this.getCell(cellId).solverPossibilites.clear();
        this.getAllRelatedCells(cellId, true).forEach((cell) => {
            cell.solverPossibilites.delete(possibility.toString());
        })
    }

    /**
     * For a given cellId, returs all of the cells that share a row, column or box with that cell, including the cell itself.
     * @param {number} cellId - the cell index
     * @returns {Cell[]}
     */
    getAllRelatedCells(cellId, excludeThisCell = false) {
        const related = [
            ...this.getCellsInSameRow(cellId),
            ...this.getCellsInSameColumn(cellId),
            ...this.getCellsInSameBox(cellId),
        ];
        if (!excludeThisCell) {
            return related;
        }
        return related.filter(cell => cell.id !== cellId);
    }

    /**
     * Get all the cells in the same row as the cell id.
     * @param {number} cellId
     * @returns {Cell[]}
     */
    getCellsInSameRow(cellId, excludeThisCell = false) {
        const cell = this.getCell(cellId);
        return this.cells.filter(c => c.row == cell.row && (!excludeThisCell || c != cell));
    }

    /**
     * Get all the cells in the same column as the cell id
     * @param {number} cellId
     * @returns {Cell[]}
     */
    getCellsInSameColumn(cellId, excludeThisCell = false) {
        const cell = this.getCell(cellId);
        return this.cells.filter(c => c.column == cell.column && (!excludeThisCell || c != cell));
    }

    /**
     * Get all the cells in the same box as the cell id
     * @param {number} cellId
     * @returns {Cell[]}
     */
    getCellsInSameBox(cellId, excludeThisCell = false) {
        const cell = this.getCell(cellId);
        return this.cells.filter(c => c.box == cell.box && (!excludeThisCell || c != cell));
    }

    /**
     * Gets the number of columns in a box (the 6x6 game is a special case where the boxes are not square)
     * @returns
     */
    getBoxColumnCount() {
        return this.boxColumnCount;
    }
    /**
     * Gets the number of rows in a box. (the 6x6 game is a special case where the boxes are not square)
     * @returns
     */
    getBoxRowCount() {
        return this.boxRowCount;
    }

    /**
     * Returns a Set of cell IDs that share a row, column, or box with the given cellId (excluding the cell itself).
     */
    getRelatedCellIdSet(cellId: number): Set<number> {
        const ids = new Set<number>();
        const related = this.getAllRelatedCells(cellId, true);
        for (const cell of related) {
            ids.add(cell.id);
        }
        return ids;
    }

    private computeBoxColumnCount() {
        if (this.size == 6) {
            return 2;
        }
        return Math.sqrt(this.size);
    }

    private computeBoxRowCount() {
        if (this.size == 6) {
            return 3;
        }
        return Math.sqrt(this.size);
    }

    /**
     * Clear the cell
     * @param {number} cellId
     */
    clearCell(cellId) {
        const cell = this.cells[cellId];
        if (!cell) {
            return;
        }
        const removedGuess = !cell.isGiven && Boolean(cell.guess);
        cell.clear();
        if (removedGuess) {
            this.calculateAllPossibilities();
        }
        this.evaluateUnitCompletion(cellId, false);
    }
    /**
     * Reset the game to the starting position
     */
    reset() {
        this.cells.forEach(cell => cell.clear())
        this.calculateAllPossibilities();
        this.recalcAllUnitCompletions(false);
        this.resetSolverTracking();
    }
    /**
     * Remove all possibilities
     */
    resetPossibilities() {
        this.cells.forEach(cell => cell.resetPossibilities())
    }
    /**
     * Undo the last move
     */
    undo() {
        const theUndo = this.undos.pop();
        if (theUndo) {
            this.restoreGame(theUndo)
        }
    }

    /**
     * Set the game to the value specified in game string.
     * @param {string} unsolvedString - Serialized board string (e.g. "1|||3|P123|").
     * @param {boolean} syncSolverPossibilities - When true, copy any provided possibilities ("P...") into solverPossibilites.
     */
    restoreGame(unsolvedString: string, syncSolverPossibilities: boolean = false) {
        const cellValues = unsolvedString.split("|")
        cellValues.forEach((cellValue, idx) => {
            const theCell = this.getCell(idx)
            // Old saved games had extra trailing space
            if (!theCell) {
                return;
            }
            theCell.clear()
            theCell.solverPossibilites.clear();
            if (cellValue[0] == "P") {
                cellValue = cellValue.substring(1)
                cellValue.split("").forEach((val) => {
                    if (val.trim() != "") {
                        theCell.togglePossibility(val.trim())
                    }
                })
                if (syncSolverPossibilities) {
                    theCell.solverPossibilites = new Set(theCell.possibilities);
                }
            } else {
                if (cellValue == "0") {
                    cellValue = "";
                }
                theCell.addGuess(cellValue.trim())
            }
        })
        this.recalcAllUnitCompletions(false);
        this.resetSolverTracking();
    }
    /**
     * Gets the rows
     * @returns {Cell[][]}
     */
    getRows() {
        return this.cells.reduce<Cell[][]>((acc, cell, idx) => {
            if (!acc[cell.row]) {
                acc[cell.row] = [];
            }
            acc[cell.row].push(cell);
            return acc;
        }, [])

    }
    /**
     * Gets the columns for the game
     * @returns {Cell[][]}
     */
    getColumns() {
        return this.cells.reduce<Cell[][]>((acc, cell, idx) => {
            if (!acc[cell.column]) {
                acc[cell.column] = [];
            }
            acc[cell.column].push(cell);
            return acc;
        }, [])

    }

    /**
     * Gets the boxes for the game - index 0 in the array is the box in the top left corner of the game.
     * @returns {Cell[][]}
     */
    getBoxes() {
        return this.cells.reduce<Cell[][]>((acc, cell, idx) => {
            if (!acc[cell.box]) {
                acc[cell.box] = [];
            }
            acc[cell.box].push(cell);
            return acc;
        }, [])

    }

    /**
     * Check if any rows, columns or boxes are completed from this cell being filled in.
     * @param cellId the cell being evaluated
     * @param shouldAnimate Should trigger callbacks to animate completed cells
     * @returns
     */
    private evaluateUnitCompletion(cellId: number, shouldAnimate: boolean) {
        const cell = this.cells[cellId];
        if (!cell) {
            return;
        }

        const newlyCompleted: CompletedUnitPayload[] = [];
        const checkUnit = (type: SudokuUnitType, index: number, unitCells: Cell[], completedSet: Set<number>) => {
            if (!unitCells || unitCells.length === 0) {
                return;
            }
            const isComplete = unitCells.every(unitCell => unitCell.isCorrect());
            const wasComplete = completedSet.has(index);
            if (isComplete) {
                if (!wasComplete) {
                    completedSet.add(index);
                    newlyCompleted.push({
                        type,
                        index,
                        cellIds: unitCells.map(unitCell => unitCell.id),
                    });
                }
            } else if (wasComplete) {
                completedSet.delete(index);
            }
        };

        checkUnit("row", cell.row, this.getCellsInSameRow(cellId), this.completedRows);
        checkUnit("column", cell.column, this.getCellsInSameColumn(cellId), this.completedColumns);
        checkUnit("box", cell.box, this.getCellsInSameBox(cellId), this.completedBoxes);

        if (shouldAnimate && newlyCompleted.length && this.onUnitsCompleted && !this.solverRunActive) {
            this.onUnitsCompleted({
                originCellId: cellId,
                units: newlyCompleted,
            });
        }
    }

    private recalcAllUnitCompletionsInternal(shouldAnimate: boolean) {
        this.completedRows.clear();
        this.completedColumns.clear();
        this.completedBoxes.clear();

        if (!this.cells.length) {
            return;
        }

        const completedUnits: CompletedUnitPayload[] = [];
        const evaluateCollection = (type: SudokuUnitType, collections: Cell[][], completedSet: Set<number>) => {
            collections.forEach((unitCells, index) => {
                if (!unitCells || unitCells.length === 0) {
                    return;
                }
                const isComplete = unitCells.every(cell => cell.isCorrect());
                if (isComplete) {
                    completedSet.add(index);
                    completedUnits.push({
                        type,
                        index,
                        cellIds: unitCells.map(cell => cell.id),
                    });
                }
            });
        };

        evaluateCollection("row", this.getRows(), this.completedRows);
        evaluateCollection("column", this.getColumns(), this.completedColumns);
        evaluateCollection("box", this.getBoxes(), this.completedBoxes);

        if (shouldAnimate && completedUnits.length && this.onUnitsCompleted) {
            const originCellId = completedUnits[0].cellIds[0];
            this.onUnitsCompleted({
                originCellId,
                units: completedUnits,
            });
        }
    }

    recalcAllUnitCompletions(shouldAnimate: boolean = false) {
        this.recalcAllUnitCompletionsInternal(shouldAnimate);
    }

    /**
     * gets a cell
     * @param {number} id
     * @returns {Cell}
     */
    getCell(id) {
        return this.cells[id]
    }

    public markAllCellsDirty() {
        for (const cell of this.cells) {
            cell.markDirty();
        }
    }

    private markCellsDirtyByIds(cellIds: Iterable<number | null | undefined>) {
        for (const cellId of cellIds) {
            if (cellId === null || cellId === undefined) {
                continue;
            }
            const cell = this.cells[cellId];
            if (cell) {
                cell.markDirty();
            }
        }
    }

    private cellMatchesActiveNumber(cell: Cell, activeNumber: string | null): boolean {
        if (!activeNumber) {
            return false;
        }
        if (cell.isGiven && cell.answer === activeNumber) {
            return true;
        }
        if (cell.guess === activeNumber) {
            return true;
        }
        return cell.possibilities.has(activeNumber);
    }

    private markCellsDirtyForActiveNumberChange(prev: string | null, next: string | null) {
        if (prev === next) {
            return;
        }
        for (const cell of this.cells) {
            if (this.cellMatchesActiveNumber(cell, prev) || this.cellMatchesActiveNumber(cell, next)) {
                cell.markDirty();
            }
        }
    }

    private getRenderStateSnapshot(): RenderStateSnapshot {
        const activeCellId = this.activeCell ? this.activeCell.id : null;
        const relatedCellIds = (activeCellId !== null && this.highlightRelatedCells)
            ? this.getRelatedCellIdSet(activeCellId)
            : null;
        return {
            possibilitiesInGrid: this.possibilitiesInGrid,
            showIncorrectMoves: this.showIncorrectMoves,
            showSolution: this.showSolution,
            highlightActiveNumber: this.highlightActiveNumber,
            activeNumber: this.activeNumber,
            highlightActiveCell: this.highlightActiveCell,
            activeCellId,
            relatedCellIds,
        };
    }

    private applyRenderStateDiff(nextState: RenderStateSnapshot) {
        const prevState = this.lastRenderState;
        if (!prevState) {
            this.markAllCellsDirty();
            return;
        }

        const requiresFullRefresh = prevState.showSolution !== nextState.showSolution
            || prevState.possibilitiesInGrid !== nextState.possibilitiesInGrid
            || prevState.showIncorrectMoves !== nextState.showIncorrectMoves
            || prevState.highlightActiveNumber !== nextState.highlightActiveNumber;

        if (requiresFullRefresh) {
            this.markAllCellsDirty();
            return;
        }

        const highlightActiveCellChanged = prevState.highlightActiveCell !== nextState.highlightActiveCell
            || prevState.activeCellId !== nextState.activeCellId;
        if (highlightActiveCellChanged && (prevState.highlightActiveCell || nextState.highlightActiveCell)) {
            const highlightedCellIds: Array<number | null> = [prevState.activeCellId, nextState.activeCellId];
            if (prevState.highlightActiveCell && prevState.activeCellId !== null) {
                highlightedCellIds.push(...(this.getCageForCell(prevState.activeCellId)?.cells ?? []));
            }
            if (nextState.highlightActiveCell && nextState.activeCellId !== null) {
                highlightedCellIds.push(...(this.getCageForCell(nextState.activeCellId)?.cells ?? []));
            }
            this.markCellsDirtyByIds(highlightedCellIds);
        }

        // Mark related cells dirty when active cell changes or highlight setting toggles
        const relatedSetsChanged = prevState.activeCellId !== nextState.activeCellId
            || (prevState.relatedCellIds !== null) !== (nextState.relatedCellIds !== null);
        if (relatedSetsChanged) {
            if (prevState.relatedCellIds) {
                this.markCellsDirtyByIds(prevState.relatedCellIds);
            }
            if (nextState.relatedCellIds) {
                this.markCellsDirtyByIds(nextState.relatedCellIds);
            }
        }

        if (prevState.activeNumber !== nextState.activeNumber
            && nextState.highlightActiveNumber !== "false"
            && !nextState.showSolution) {
            this.markCellsDirtyForActiveNumberChange(prevState.activeNumber, nextState.activeNumber);
        }
    }

    /**
     * Update the game board on the page
     */
    updateGameBoardUI(isHintUpdate: boolean = false) {
        if (!isHintUpdate) {
            Hinter.close(!Hinter.executedHint); // Keep the hint if executed
        }
        const renderState = this.getRenderStateSnapshot();
        this.applyRenderStateDiff(renderState);
        this.lastRenderState = renderState;

        const dirtyCellIds = this.cells
            .filter(cell => cell.isDirty)
            .map(cell => cell.id);
        if (!dirtyCellIds.length) {
            return;
        }
        this.enqueueCellsForUpdate(dirtyCellIds);
    }

    clearTemporaryHighlights(): boolean {
        let changed = false;
        for (const cell of this.cells) {
            if (cell.isHint || cell.isDescription || cell.isIndication) {
                cell.isHint = false;
                cell.isDescription = false;
                cell.isIndication = false;
                cell.markDirty();
                changed = true;
            }
        }
        return changed;
    }

    private getCellElement(cellId: number): HTMLTableCellElement | null {
        let element = this.cellElements[cellId] ?? null;
        if (!element || !element.isConnected) {
            element = document.getElementById(`T${cellId}`) as HTMLTableCellElement | null;
            this.cellElements[cellId] = element ?? undefined;
        }
        return element;
    }

    private enqueueCellsForUpdate(cellIds: Iterable<number>) {
        let added = false;
        for (const cellId of cellIds) {
            if (!Number.isInteger(cellId) || cellId < 0 || cellId >= this.cells.length) {
                continue;
            }
            if (this.pendingCellSet.has(cellId)) {
                continue;
            }
            this.pendingCellSet.add(cellId);
            this.pendingCellUpdates.push(cellId);
            added = true;
        }

        if (added) {
            this.schedulePendingFlush();
        }
    }

    private schedulePendingFlush() {
        if (this.isFlushScheduled) {
            return;
        }
        this.isFlushScheduled = true;
        window.requestAnimationFrame(this.flushPendingUpdates);
    }

    private flushPendingUpdates = () => {
        let processed = 0;

        const activeCellId = this.activeCell ? this.activeCell.id : null;
        const relatedCellIds = (activeCellId !== null && this.highlightRelatedCells)
            ? this.getRelatedCellIdSet(activeCellId)
            : null;
        const renderContext = {
            possibilitiesInGrid: this.possibilitiesInGrid,
            showIncorrectMoves: this.showIncorrectMoves,
            showSolution: this.showSolution,
            gridBoxHeight: this.boxRowCount,
            gridBoxWidth: this.boxColumnCount,
            activeNumber: this.activeNumber,
            highlightActiveNumber: this.highlightActiveNumber,
            activeCellId,
            highlightActiveCell: this.highlightActiveCell,
            relatedCellIds,
        };
        const highlightedCageId = renderContext.highlightActiveCell && activeCellId !== null
            ? this.getCageForCell(activeCellId)?.id
            : undefined;

        while (this.pendingCellUpdates.length > 0 && processed < UI_UPDATE_CHUNK_SIZE) {
            const cellId = this.pendingCellUpdates.shift();
            if (cellId === undefined) {
                break;
            }

            this.pendingCellSet.delete(cellId);

            const cellElement = this.getCellElement(cellId);
            const cell = this.getCell(cellId);
            if (cellElement && cell.isDirty) {
                const cage = this.getCageForCell(cellId);
                const cageLayout = this.getCageBordersForCell(cellId);
                const cageData = cage ? {
                    sum: cage.sum,
                    borders: cageLayout.borders,
                    corners: cageLayout.corners,
                    isOrigin: cage.originCell === cellId,
                    isHighlighted: cage.id === highlightedCageId,
                } : undefined;

                cellElement.innerHTML = cell.render({
                    ...renderContext,
                    cageData,
                });
                cell.clearDirty();
            }
            processed++;
        }

        if (this.pendingCellUpdates.length > 0) {
            window.requestAnimationFrame(this.flushPendingUpdates);
        } else {
            this.isFlushScheduled = false;
        }
    };

    public runAfterPendingFlush(callback: () => void) {
        const waitForFlush = () => {
            if (this.isFlushScheduled) {
                window.requestAnimationFrame(waitForFlush);
            } else {
                callback();
            }
        };
        window.requestAnimationFrame(waitForFlush);
    }

    /**
     * Does the game have any moves to undo
     * @returns
     */
    hasUndos() {
        return this.undos.length > 0
    }

    /**
     * Determines each cells possible values by finding all of the related cells with values and
     * removing them from the possibilities.
     */
    calculateAllPossibilities(preserveSolverPossibilities: boolean = false) {
        this.cells.forEach((cell, idx) => {
            // If a cell is already filled in then assume it has no possibilities.
            if (cell.guess) {
                cell.solverPossibilites = new Set();
            } else {
                const usedNumbers = this.getAllRelatedCells(idx).reduce((used, relatedCell) => {
                    if (relatedCell.guess) {
                        used.add(relatedCell.guess)
                    }
                    return used;
                }, new Set())
                const basicPossibilities = this.validInputs.difference(usedNumbers);
                const hasKnownAnswer = Boolean(cell.answer && cell.answer !== "0");
                const canPreserveSolverPossibilities = preserveSolverPossibilities
                    && cell.solverPossibilites.size > 0
                    && (!hasKnownAnswer || cell.solverPossibilites.has(cell.answer));

                if (canPreserveSolverPossibilities) {
                    // Solver candidates may contain advanced-strategy eliminations
                    // that are intentionally absent from the user's pencil marks.
                    // Keep those eliminations while enforcing current board values.
                    cell.solverPossibilites = new Set(
                        [...cell.solverPossibilites].filter(possibility => basicPossibilities.has(possibility))
                    );
                } else {
                    cell.solverPossibilites = basicPossibilities;
                }
            }
        })
        return this.cells;
    }

    /**
     * Validates user possibilities against solver possibilities and the correct answer.
     * When allowSubsets is true, user possibilities may be a subset of solver possibilities
     * as long as they still include the correct answer.
     */
    validateUserPossibilities(shouldResetInvalidCells: boolean = false, allowSubsets: boolean = false) {
        // First calculate the correct solver possibilities
        this.calculateAllPossibilities(true);

        const invalidCells: Cell[] = [];
        const subsetCells: Cell[] = [];

        // Check each cell for validation
        for (const cell of this.cells) {
            // Skip cells that already have guesses
            if (cell.guess) {
                continue;
            }

            const hasUserPossibilities = cell.possibilities.size > 0;
            const matchesSolver = this.setsEqual(cell.possibilities, cell.solverPossibilites);
            const isSubset = this.isSetSubset(cell.possibilities, cell.solverPossibilites);
            const includesAnswer = !cell.answer || cell.answer === "0" || cell.possibilities.has(cell.answer);
            const isInvalid = !hasUserPossibilities
                || !includesAnswer
                || (!allowSubsets && !matchesSolver)
                || (allowSubsets && !isSubset);

            if (isInvalid) {
                invalidCells.push(cell);
                if (shouldResetInvalidCells) {
                    cell.replacePossibilitiesWithSolverPossibilities();
                }
            } else if (allowSubsets && !matchesSolver) {
                subsetCells.push(cell);
            }
        }

        return { invalidCells, subsetCells };
    }

    /**
     * Use user possibilities as solver possibilities for any cell that has them.
     */
    applyUserPossibilitiesToSolver() {
        this.cells.forEach((cell) => {
            if (cell.guess || cell.possibilities.size === 0) {
                return;
            }
            cell.solverPossibilites = new Set(cell.possibilities);
        });
    }

    /**
     * Creates a UI update function that highlights cells and shows hint text
     * @param {string} hintText - The text to display as the hint
     * @param {Cell} hintCell - The cell that contains the hint
     * @param {boolean} isHint - Whether to highlight the hint cell
     * @param {Cell[]} descriptionCells - Cells to highlight with description color (optional, defaults to empty array)
     * @param {Cell[]} indicationCells - Cells to highlight with indication color (optional, defaults to empty array)
     * @param {Cell|null} setActiveCell - Cell to set as active (optional)
     * @param {Cell|null} addGuess - Cell to add guess to (optional)
     * @param {string[]|null} eliminateCandidates - Candidates to eliminate (optional)
     * @param {Cell[]|null} eliminateFromCells - Cells to eliminate candidates from (optional)
     * @returns {Function} A function that when called performs the UI update and cleanup
     */
    createUIUpdateFunction(hintText: string, hintCell: Cell, isHint: boolean, descriptionCells: Cell[] = [], indicationCells: Cell[] = [], setActiveCell: Cell | null, addGuess: Cell | null, eliminateCandidates: string[] | null = null, eliminateFromCells: Cell[] | null = null) {
        return (() => {
            // Make actual updates to the game
            if (setActiveCell) {
                this.setActiveCell(setActiveCell.id);
            }
            if (addGuess) {
                this.addGuess(addGuess.id, addGuess.answer);
            }
            if (eliminateCandidates && eliminateFromCells) {
                // Eliminate specific candidates from specific cells
                eliminateFromCells.forEach((cell) => {
                    eliminateCandidates.forEach((candidate) => {
                        cell.removePossibility(candidate);
                        cell.solverPossibilites.delete(candidate);
                    });
                });
            }

            // Make the temporary UI changes
            // Highlight the cells and show hint text
            descriptionCells.forEach((cell) => {
                cell.isDescription = true;
                cell.markDirty();
            });
            indicationCells.forEach((cell) => {
                cell.isIndication = true;
                cell.markDirty();
            });
            if (isHint) {
                hintCell.isHint = true;
                hintCell.markDirty();
            }
            this.updateGameBoardUI(true);
            this.runAfterPendingFlush(() => {
                if (isHint) {
                    hintCell.isHint = false;
                    hintCell.markDirty();
                }
                descriptionCells.forEach((cell) => {
                    cell.isDescription = false;
                    cell.markDirty();
                });
                indicationCells.forEach((cell) => {
                    cell.isIndication = false;
                    cell.markDirty();
                });
            });
            return hintText;
        }).bind(this);
    }

    private getStrategyLink(strategyKeyOrName: string): string {
        if (!strategyKeyOrName) {
            return "";
        }

        const directMatch = this.strategies[strategyKeyOrName];
        if (directMatch) {
            return directMatch.link
                ? `<a class="text-decoration-none" href="${directMatch.link}" target="_blank" rel="noopener noreferrer">${directMatch.name}</a>`
                : directMatch.name;
        }

        return strategyKeyOrName;
    }

    /**
     * Shows why this is a Full House situation
     */
    descriptionFullHouse(hint) {
        const cell = this.getCell(hint.id);
        const answer = hint.answer;

        // Find which unit (row, column, or box) makes this a full house
        const cellsInRow = this.getCellsInSameRow(cell.id);
        const cellsInColumn = this.getCellsInSameColumn(cell.id);
        const cellsInBox = this.getCellsInSameBox(cell.id);

        let unitCells = cellsInRow;
        let unitType = "row";

        // Check which unit has 8 filled cells
        if (cellsInColumn.filter(c => c.guess || c.isGiven).length === this.size - 1) {
            unitCells = cellsInColumn;
            unitType = "column";
        } else if (cellsInBox.filter(c => c.guess || c.isGiven).length === this.size - 1) {
            unitCells = cellsInBox;
            unitType = "box";
        }

        const filledCells = unitCells.filter(c => c.guess || c.isGiven);
        const strategyLabel = this.getStrategyLink(hint.strategy);

        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - ${unitType} missing one number`,
                cell,
                false,
                filledCells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `Only one number is missing: ${answer}`,
                cell,
                true,
                [],
                [],
                cell,
                cell
            )
        ];
    }

    /**
     * Shows why this is a Naked Single
     */
    descriptionNakedSingle(hint) {
        const cell = this.getCell(hint.id);
        const answer = hint.answer;
        const relatedCells = this.getAllRelatedCells(cell.id);
        const strategyLabel = this.getStrategyLink(hint.strategy);

        // Find one representative cell for each number that eliminates possibilities
        const eliminatingCells: Cell[] = [];
        const numbersFound = new Set<string>();

        // Look for cells that contain each possible number (excluding the answer)
        this.validInputs.forEach((number) => {
            if (number !== answer && !numbersFound.has(number)) {
                const eliminatingCell = relatedCells.find(c =>
                    (c.guess === number) || (c.isGiven && c.answer === number)
                );
                if (eliminatingCell) {
                    eliminatingCells.push(eliminatingCell);
                    numbersFound.add(number);
                }
            }
        });

        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - only one possible value`,
                cell,
                false,
                eliminatingCells,
                [],
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `All other numbers are eliminated by neighboring cells`,
                cell,
                false,
                [],
                eliminatingCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `The only possibility is ${answer}`,
                cell,
                true,
                [],
                [],
                cell,
                cell
            )
        ];
    }

    /**
     * Shows why this is a Hidden Single
     */
    descriptionHiddenSingle(hint) {
        const cell = this.getCell(hint.id);
        const answer = hint.answer;
        const strategyLabel = this.getStrategyLink(hint.strategy);

        // Find the unit where this is the only cell that can contain the answer
        const units = [
            { type: 'row', cells: this.getCellsInSameRow(cell.id) },
            { type: 'column', cells: this.getCellsInSameColumn(cell.id) },
            { type: 'box', cells: this.getCellsInSameBox(cell.id) }
        ];

        let targetUnit = units[0];
        for (const unit of units) {
            const candidateCells = unit.cells.filter(c =>
                !c.guess && c.solverPossibilites.has(answer)
            );
            if (candidateCells.length === 1) {
                targetUnit = unit;
                break;
            }
        }

        const descriptionCells: Cell[] = [];
        const indicationCells: Cell[] = [];

        // Find what prevents other cells in the unit from containing this number
        targetUnit.cells.forEach((relatedCell) => {
            if (relatedCell === cell) return; // Skip the hint cell itself

            if (relatedCell.guess || relatedCell.isGiven) {
                // This cell already contains the number
                descriptionCells.push(relatedCell);
                return;
            }

            // If the cell is already added to descriptionCells, skip it
            if (descriptionCells.includes(relatedCell)) {
                return;
            }

            // This cell can't contain the number due to conflicts elsewhere
            // Always add the blocked cell to description cells
            descriptionCells.push(relatedCell);
            const cellsInRow = this.getCellsInSameRow(relatedCell.id, true);
            const excludingCellInRow = cellsInRow.find(c => c.guess == answer || (c.answer == answer && c.isGiven));
            if (excludingCellInRow) {
                descriptionCells.push(...cellsInRow);
                indicationCells.push(excludingCellInRow);
                return;
            }
            const cellsInColumn = this.getCellsInSameColumn(relatedCell.id, true);
            const excludingCellInColumn = cellsInColumn.find(c => c.guess == answer || (c.answer == answer && c.isGiven));
            if (excludingCellInColumn) {
                descriptionCells.push(...cellsInColumn);
                indicationCells.push(excludingCellInColumn);
                return;
            }
            const cellsInBox = this.getCellsInSameBox(relatedCell.id, true);
            const excludingCellInBox = cellsInBox.find(c => c.guess == answer || (c.answer == answer && c.isGiven));
            if (excludingCellInBox) {
                descriptionCells.push(...cellsInBox);
                indicationCells.push(excludingCellInBox);
                return;
            }
        });

        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - only option in ${targetUnit.type}`,
                cell,
                false,
                targetUnit.cells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `Other cells in ${targetUnit.type} cannot be ${answer}`,
                cell,
                false,
                descriptionCells,
                indicationCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Only this cell can be ${answer} in this ${targetUnit.type}`,
                cell,
                true,
                [],
                [],
                cell,
                cell
            )
        ];
    }

    /**
     * Shows Locked Candidates Type 1 (Pointing)
     */
    descriptionLockedCandidatesType1(hint) {
        const cell = this.getCell(hint.id);
        const candidate = hint.answer;
        const candidateCells = hint.data.candidateCellIds.map(id => this.getCell(id));
        const eliminationCells = hint.data.eliminationCellIds.map(id => this.getCell(id));
        const unitType = hint.data.unitType;
        const strategyLabel = this.getStrategyLink(hint.strategy);
        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - Only these cells in box ${hint.data.boxIndex+1} can contain ${candidate}`,
                cell,
                false,
                candidateCells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `This eliminates ${candidate} from the rest of the cells in ${unitType} ${hint.data.unitIndex+1}`,
                cell,
                false,
                candidateCells,
                eliminationCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${candidate} from highlighted cells`,
                cell,
                false,
                candidateCells,
                eliminationCells,
                cell,
                null,
                [candidate],
                eliminationCells
            )
        ];
    }

    /**
     * Shows Locked Candidates Type 2 (Claiming)
     */
    descriptionLockedCandidatesType2(hint) {
        const cell = this.getCell(hint.id);
        const candidate = hint.answer;
        const candidateCells = hint.data.candidateCellIds.map(id => this.getCell(id));
        const eliminationCells = hint.data.eliminationCellIds.map(id => this.getCell(id));
        const unitType = hint.data.unitType;
        const strategyLabel = this.getStrategyLink(hint.strategy);
        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - Only these cells in ${unitType} ${hint.data.unitIndex+1} can contain ${candidate}`,
                cell,
                false,
                candidateCells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `This eliminates ${candidate} from the rest of the cells in this box`,
                cell,
                false,
                candidateCells,
                eliminationCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${candidate} from highlighted cells`,
                cell,
                false,
                candidateCells,
                eliminationCells,
                cell,
                null,
                [candidate],
                eliminationCells
            )
        ];
    }

    /**
     * Shows Naked Pair elimination
     */
    descriptionNakedPair(hint) {
        const cell = this.getCell(hint.id);
        const pairCells = hint.data.cellIds.map(id => this.getCell(id));
        const candidates = hint.data.candidates;
        const unitType = hint.data.unitType;
        const eliminationCells = hint.data.eliminationCellIds.map(id => this.getCell(id));
        const strategyLabel = this.getStrategyLink(hint.strategy);

        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - two cells with only two candidates`,
                cell,
                false,
                pairCells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `This eliminates ${candidates.join(' and ')} from other cells in this ${unitType}`,
                cell,
                false,
                pairCells,
                eliminationCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${candidates.join(', ')} from highlighted cells`,
                cell,
                false,
                pairCells,
                eliminationCells,
                cell,
                null,
                candidates,
                eliminationCells
            )
        ];
    }

    /**
     * Shows Hidden Pair elimination
     */
    descriptionHiddenPair(hint) {
        const cell = this.getCell(hint.id);
        const pairCells = hint.data.cellIds.map(id => this.getCell(id));
        const candidates = hint.data.candidates;
        const unitType = hint.data.unitType;
        const eliminationCandidates = hint.data.eliminationCandidates;
        const strategyLabel = this.getStrategyLink(hint.strategy);
        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - Only these two cells in ${unitType} ${hint.data.unitIndex+1} can contain values ${candidates.join(', ')}`,
                cell,
                false,
                pairCells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `This eliminates other candidates (${eliminationCandidates.join(', ')}) from these cells`,
                cell,
                false,
                [],
                pairCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${eliminationCandidates.join(', ')} from highlighted cells`,
                cell,
                false,
                [],
                pairCells,
                cell,
                null,
                eliminationCandidates,
                pairCells
            )
        ];
    }

    /**
     * Shows Naked Triple elimination
     */
    descriptionNakedTriple(hint) {
        const cell = this.getCell(hint.id);
        const tripleCells = hint.data.cellIds.map(id => this.getCell(id));
        const candidates = hint.data.candidates;
        const unitType = hint.data.unitType;
        const eliminationCells = hint.data.eliminationCellIds.map(id => this.getCell(id));
        const strategyLabel = this.getStrategyLink(hint.strategy);
        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - Three cells in ${unitType} ${hint.data.unitIndex+1} can only contain three candidates`,
                cell,
                false,
                    tripleCells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `This eliminates ${candidates.join(', ')} from other cells in this ${unitType}`,
                cell,
                false,
                tripleCells,
                eliminationCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${candidates.join(', ')} from highlighted cells`,
                cell,
                false,
                tripleCells,
                eliminationCells,
                cell,
                null,
                candidates,
                eliminationCells
            )
        ];
    }

    /**
     * Shows Hidden Triple elimination
     */
    descriptionHiddenTriple(hint) {
        const cell = this.getCell(hint.id);
        const tripleCells = hint.data.cellIds.map(id => this.getCell(id));
        const candidates = hint.data.candidates;
        const unitType = hint.data.unitType;
        const eliminationCandidates = hint.data.eliminationCandidates;
        const strategyLabel = this.getStrategyLink(hint.strategy);

        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - three candidates in three cells only`,
                cell,
                false,
                tripleCells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `This eliminates other candidates (${eliminationCandidates.join(', ')}) from these cells`,
                cell,
                false,
                [],
                tripleCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${eliminationCandidates.join(', ')} from highlighted cells`,
                cell,
                false,
                [],
                tripleCells,
                cell,
                null,
                eliminationCandidates,
                tripleCells
            )
        ];
    }

    /**
     * Shows Naked Quadruple elimination
     */
    descriptionNakedQuadruple(hint) {
        const cell = this.getCell(hint.id);
        const quadrupleCells = hint.data.cellIds.map(id => this.getCell(id));
        const candidates = hint.data.candidates;
        const unitType = hint.data.unitType;
        const eliminationCells = hint.data.eliminationCellIds.map(id => this.getCell(id));
        const strategyLabel = this.getStrategyLink(hint.strategy);

        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - four cells with four candidates`,
                cell,
                false,
                quadrupleCells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `This eliminates ${candidates.join(', ')} from other cells in the same ${unitType}`,
                cell,
                false,
                quadrupleCells,
                eliminationCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${candidates.join(', ')} from highlighted cells`,
                cell,
                false,
                quadrupleCells,
                eliminationCells,
                cell,
                null,
                candidates,
                eliminationCells
            )
        ];
    }

    /**
     * Shows Hidden Quadruple elimination
     */
    descriptionHiddenQuadruple(hint) {
        const cell = this.getCell(hint.id);
        const quadrupleCells = hint.data.cellIds.map(id => this.getCell(id));
        const candidates = hint.data.candidates;
        const unitType = hint.data.unitType;
        const eliminationCandidates = hint.data.eliminationCandidates;
        const strategyLabel = this.getStrategyLink(hint.strategy);

        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - four candidates in four cells only`,
                cell,
                false,
                quadrupleCells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `This eliminates other candidates (${eliminationCandidates.join(', ')}) from these four cells`,
                cell,
                false,
                [],
                quadrupleCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${eliminationCandidates.join(', ')} from highlighted cells`,
                cell,
                false,
                [],
                quadrupleCells,
                cell,
                null,
                eliminationCandidates,
                quadrupleCells
            )
        ];
    }

    /**
     * Shows X-Wing elimination pattern
     */
    descriptionXWing(hint) {
        const cell = this.getCell(hint.id);
        const candidate = hint.answer;
        const xwingCells = hint.data.cellIds.map(id => this.getCell(id));
        const eliminationCells = hint.data.eliminationCellIds.map(id => this.getCell(id));
        const patternType = hint.data.patternType;
        const strategyLabel = this.getStrategyLink(hint.strategy);

        const baseUnits = patternType === 'row-based' ? 'rows' : 'columns';
        const coverUnits = patternType === 'row-based' ? 'columns' : 'rows';
        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - There is an X-wing in the highlighted cells`,
                cell,
                false,
                xwingCells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `These ${baseUnits} have two cells with ${candidate} as a possiblity and the cells form a box.`,
                cell,
                false,
                xwingCells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `Therefore, ${candidate} can be eliminated from other cells in the same ${coverUnits}`,
                cell,
                false,
                xwingCells,
                eliminationCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${candidate} from highlighted cells`,
                cell,
                false,
                xwingCells,
                eliminationCells,
                cell,
                null,
                [candidate],
                eliminationCells
            )
        ];
    }

    descriptionSwordfish(hint) {
        return this.descriptionFish(hint, "Swordfish");
    }

    descriptionJellyfish(hint) {
        return this.descriptionFish(hint, "Jellyfish");
    }

    descriptionFish(hint, label: string) {
        const cell = this.getCell(hint.id);
        const candidate = hint.answer;
        const fishCells = hint.data.cellIds.map(id => this.getCell(id));
        const eliminationCells = hint.data.eliminationCellIds.map(id => this.getCell(id));
        const patternType = hint.data.patternType;
        const fishSize = hint.data.fishSize;
        const strategyLabel = this.getStrategyLink(hint.strategy);

        const baseUnits = patternType === 'row-based' ? 'rows' : 'columns';
        const coverUnits = patternType === 'row-based' ? 'columns' : 'rows';
        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - ${label} pattern in highlighted cells`,
                cell,
                false,
                fishCells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `${fishSize} ${baseUnits} share ${fishSize} ${coverUnits} for candidate ${candidate}.`,
                cell,
                false,
                fishCells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `Therefore, ${candidate} can be eliminated from other cells in the same ${coverUnits}.`,
                cell,
                false,
                fishCells,
                eliminationCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${candidate} from highlighted cells`,
                cell,
                false,
                fishCells,
                eliminationCells,
                cell,
                null,
                [candidate],
                eliminationCells
            )
        ];
    }

    /**
     * Shows Skyscraper elimination pattern
     */
    descriptionSkyscraper(hint) {
        const cell = this.getCell(hint.id);
        const candidate = hint.answer;
        const towerCells = hint.data.towerCellIds.map(id => this.getCell(id));
        const baseCells = hint.data.baseCellIds.map(id => this.getCell(id));
        const eliminationCells = hint.data.eliminationCellIds.map(id => this.getCell(id));
        const patternType = hint.data.patternType;
        const strategyLabel = this.getStrategyLink(hint.strategy);

        const baseUnits = patternType === 'row-based' ? 'rows' : 'columns';
        const sharedUnit = patternType === 'row-based' ? 'column' : 'row';

        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - Skyscraper pattern in highlighted cells`,
                cell,
                false,
                [...baseCells, ...towerCells],
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `Two ${baseUnits} share a candidate in one ${sharedUnit}, leaving two towers.`,
                cell,
                false,
                [...baseCells, ...towerCells],
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `Therefore, ${candidate} can be eliminated from cells that see both towers.`,
                cell,
                false,
                towerCells,
                eliminationCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${candidate} from highlighted cells`,
                cell,
                false,
                towerCells,
                eliminationCells,
                cell,
                null,
                [candidate],
                eliminationCells
            )
        ];
    }

    /**
     * Shows Finned X-Wing elimination pattern
     */
    descriptionFinnedXWing(hint) {
        const cell = this.getCell(hint.id);
        const candidate = hint.answer;
        const baseCells = hint.data.baseCellIds.map(id => this.getCell(id));
        const finCells = hint.data.finCellIds.map(id => this.getCell(id));
        const eliminationCells = hint.data.eliminationCellIds.map(id => this.getCell(id));
        const patternType = hint.data.patternType;
        const strategyLabel = this.getStrategyLink(hint.strategy);

        const unitLabel = patternType === "row-based" ? "rows" : "columns";
        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - finned X-Wing pattern`,
                cell,
                false,
                [...baseCells, ...finCells],
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `Two ${unitLabel} form an X-Wing with a fin on candidate ${candidate}.`,
                cell,
                false,
                [...baseCells, ...finCells],
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `Therefore, ${candidate} can be eliminated from cells that see the fin and the opposite base.`,
                cell,
                false,
                finCells,
                eliminationCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${candidate} from highlighted cells`,
                cell,
                false,
                finCells,
                eliminationCells,
                cell,
                null,
                [candidate],
                eliminationCells
            )
        ];
    }

    /**
     * Shows Two-String Kite elimination pattern
     */
    descriptionTwoStringKite(hint) {
        const cell = this.getCell(hint.id);
        const candidate = hint.answer;
        const rowCells = hint.data.rowCellIds.map(id => this.getCell(id));
        const colCells = hint.data.colCellIds.map(id => this.getCell(id));
        const endCells = hint.data.endCellIds.map(id => this.getCell(id));
        const eliminationCells = hint.data.eliminationCellIds.map(id => this.getCell(id));
        const strategyLabel = this.getStrategyLink(hint.strategy);

        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - two-string kite pattern`,
                cell,
                false,
                [...rowCells, ...colCells],
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `Row and column each have two ${candidate}s, linked by a box.`,
                cell,
                false,
                [...rowCells, ...colCells],
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `Therefore, ${candidate} can be eliminated from cells that see both endpoints.`,
                cell,
                false,
                endCells,
                eliminationCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${candidate} from highlighted cells`,
                cell,
                false,
                endCells,
                eliminationCells,
                cell,
                null,
                [candidate],
                eliminationCells
            )
        ];
    }

    /**
     * Shows W-Wing elimination pattern
     */
    descriptionWWing(hint) {
        const cell = this.getCell(hint.id);
        const wingCells = hint.data.wingCellIds.map(id => this.getCell(id));
        const strongLinkCells = hint.data.strongLinkCellIds.map(id => this.getCell(id));
        const eliminationCells = hint.data.eliminationCellIds.map(id => this.getCell(id));
        const strongLinkCandidate = hint.data.strongLinkCandidate;
        const eliminationCandidate = hint.answer;
        const candidates = hint.data.candidates;
        const unitType = hint.data.unitType;
        const unitNumber = hint.data.unitIndex + 1;
        const strategyLabel = this.getStrategyLink(hint.strategy);

        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - matching bi-value cells with ${candidates.join(" and ")} form the wings`,
                cell,
                false,
                [],
                wingCells,
                null,
                null
            ),
            this.createUIUpdateFunction(
                `${strongLinkCandidate} appears only in the two highlighted link cells in ${unitType} ${unitNumber}, so those cells form a strong link.`,
                cell,
                false,
                wingCells,
                strongLinkCells,
                null,
                null
            ),
            this.createUIUpdateFunction(
                `One of the two wings must contain ${eliminationCandidate}, so it can be removed from cells that see both wings.`,
                cell,
                false,
                wingCells,
                eliminationCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${eliminationCandidate} from highlighted cells`,
                cell,
                false,
                wingCells,
                eliminationCells,
                cell,
                null,
                [eliminationCandidate],
                eliminationCells
            )
        ];
    }

    /**
     * Shows XY-Wing elimination pattern
     */
    descriptionXYWing(hint) {
        const cell = this.getCell(hint.id);
        const eliminationCandidate = hint.answer;
        const pivot = this.getCell(hint.data.pivotCellId);
        const pincers = hint.data.pincerCellIds.map(id => this.getCell(id));
        const eliminationCells = hint.data.eliminationCellIds.map(id => this.getCell(id));
        const wingCells = [pivot, ...pincers];
        const strategyLabel = this.getStrategyLink(hint.strategy);

        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - pivot with two pincers pattern`,
                cell,
                false,
                wingCells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `This eliminates ${eliminationCandidate} from cells that can see both pincers`,
                cell,
                false,
                wingCells,
                eliminationCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${eliminationCandidate} from highlighted cells`,
                cell,
                false,
                wingCells,
                eliminationCells,
                cell,
                null,
                [eliminationCandidate],
                eliminationCells
            )
        ];
    }

    /**
     * Shows XYZ-Wing elimination pattern
     */
    descriptionXYZWing(hint) {
        const cell = this.getCell(hint.id);
        const eliminationCandidate = hint.answer;
        const pivot = this.getCell(hint.data.pivotCellId);
        const pincers = hint.data.pincerCellIds.map(id => this.getCell(id));
        const eliminationCells = hint.data.eliminationCellIds.map(id => this.getCell(id));
        const wingCells = [pivot, ...pincers];
        const strategyLabel = this.getStrategyLink(hint.strategy);

        return [
            this.createUIUpdateFunction(
                `${strategyLabel} - pivot with three candidates pattern`,
                cell,
                false,
                wingCells,
                [],
                null,
                null
            ),
            this.createUIUpdateFunction(
                `This eliminates ${eliminationCandidate} from cells that can see all three wing cells`,
                cell,
                false,
                wingCells,
                eliminationCells,
                cell,
                null
            ),
            this.createUIUpdateFunction(
                `Eliminating ${eliminationCandidate} from highlighted cells`,
                cell,
                false,
                wingCells,
                eliminationCells,
                cell,
                null,
                [eliminationCandidate],
                eliminationCells
            )
        ];
    }

    /**
     * Brute-force solver with backtracking. Populates each cell's `answer` field when successful.
     * @returns {boolean} true if a consistent solution was found, otherwise false.
     */
    solveBruteForce(): boolean {
        console.log("Brute force solving initiated.");
        const size = this.size;
        const totalCells = this.cells.length;
        const digits = Array.from(this.validInputs.values());
        const boardSignature = this.serialize();

        const board: (string | null)[] = new Array(totalCells).fill(null);
        const rows = Array.from({ length: size }, () => new Set<string>());
        const columns = Array.from({ length: size }, () => new Set<string>());
        const boxes = Array.from({ length: size }, () => new Set<string>());

        const normalizeValue = (value: string | null | undefined): string | null => {
            if (value === null || value === undefined) {
                return null;
            }
            const trimmed = value.toString().trim();
            if (trimmed === "" || trimmed === "0") {
                return null;
            }
            return trimmed;
        };

        for (let i = 0; i < totalCells; i++) {
            const cell = this.cells[i];
            let cellValue = normalizeValue(cell.guess);

            if (!cellValue && cell.isGiven && cell.answer) {
                const answerValue = normalizeValue(cell.answer);
                if (answerValue) {
                    cellValue = answerValue;
                }
            }

            if (cellValue) {
                if (!this.validInputs.has(cellValue)) {
                    this.solverSolutionSignature = null;
                    return false;
                }

                const row = cell.row;
                const column = cell.column;
                const box = cell.box;

                if (rows[row].has(cellValue) || columns[column].has(cellValue) || boxes[box].has(cellValue)) {
                    this.solverSolutionSignature = null;
                    return false;
                }

                rows[row].add(cellValue);
                columns[column].add(cellValue);
                boxes[box].add(cellValue);
                board[i] = cellValue;
            } else {
                board[i] = null;
            }
        }

        const selectNextCell = (): { index: number; candidates: string[] } | null => {
            let bestIndex = -1;
            let bestCandidates: string[] | null = null;

            for (let i = 0; i < totalCells; i++) {
                if (board[i]) {
                    continue;
                }

                const cell = this.cells[i];
                const candidates: string[] = [];

                for (const candidate of digits) {
                    if (
                        !rows[cell.row].has(candidate) &&
                        !columns[cell.column].has(candidate) &&
                        !boxes[cell.box].has(candidate)
                    ) {
                        candidates.push(candidate);
                    }
                }

                if (candidates.length === 0) {
                    return { index: i, candidates: [] };
                }

                if (!bestCandidates || candidates.length < bestCandidates.length) {
                    bestIndex = i;
                    bestCandidates = candidates;
                    if (candidates.length === 1) {
                        break;
                    }
                }
            }

            if (bestIndex === -1) {
                return null;
            }

            return { index: bestIndex, candidates: bestCandidates! };
        };

        const solve = (): boolean => {
            const next = selectNextCell();
            if (!next) {
                return true;
            }

            const { index, candidates } = next;
            if (candidates.length === 0) {
                return false;
            }

            const cell = this.cells[index];
            for (const candidate of candidates) {
                board[index] = candidate;
                rows[cell.row].add(candidate);
                columns[cell.column].add(candidate);
                boxes[cell.box].add(candidate);

                if (solve()) {
                    return true;
                }

                board[index] = null;
                rows[cell.row].delete(candidate);
                columns[cell.column].delete(candidate);
                boxes[cell.box].delete(candidate);
            }

            return false;
        };

        const solved = solve();

        if (!solved) {
            this.solverSolutionSignature = null;
            return false;
        }

        for (let i = 0; i < totalCells; i++) {
            const solvedValue = board[i];
            if (!solvedValue) {
                this.solverSolutionSignature = null;
                return false;
            }
            const cell = this.cells[i];
            if (!this.isSolverMode && cell.answer && cell.answer !== "0" && cell.answer !== solvedValue) {
                this.solverSolutionSignature = null;
                return false;
            }
            cell.answer = solvedValue;
        }

        this.solverSolutionSignature = boardSignature;
        return true;
    }

    /**
     * Ensure a solution exists for the current board. In solver mode we attempt to compute it when needed.
     */
    ensureSolutionAvailable(): boolean {
        if (!this.isSolverMode) {
            return true;
        }

        const currentSignature = this.serialize();
        if (this.solverSolutionSignature === currentSignature) {
            return true;
        }

        return this.solveBruteForce();
    }

    /**
     * Iterate over the list of strategies attempting to fill in the puzzle.
     * If progress is made by one of the strategies, the solve function reiterates through
     * the list of strategies, because prior strategies may work once a number is found.
     * Only loops through the easier strategies.
     */
    solveEasy() {
        const strategies = [
            this.strategyFullHouse,
            this.strategyNakedSingle,
            this.strategyHiddenSingle,
        ];
        let madeProgress: any = true;
        while (madeProgress) {
            madeProgress = null;
            this.calculateAllPossibilities();
            for (var i = 0; i < strategies.length; i++) {
                madeProgress = strategies[i].bind(this)();
                if (madeProgress) {
                    this.addGuess(madeProgress.id, madeProgress.answer)
                    break;
                }
            }
        }
    }

    /**
     * Iterate over the list of strategies attempting to fill in the puzzle.
     * If progress is made by one of the strategies, the solve function reiterates through
     * the list of strategies, because prior strategies may work once a number is found.
     */
    solve(): SolverRunResult {
        this.startSolverRun();

        let madeProgress = true;
        const maxAttempts = 200; // Prevent infinite loops
        let attempts = 0;

        while (madeProgress && attempts < maxAttempts) {
            madeProgress = false;
            attempts++;

            const hint = this.hint();

            if (!hint) {
                break; // No more hints available
            }

            if (hint.description) {
                const descriptionFunctions = hint.description(hint);
                const descriptions: string[] = [];

                descriptionFunctions.forEach((descFunc) => {
                    const result = descFunc();
                    if (typeof result === "string") {
                        descriptions.push(result);
                    }
                });

                this.recordSolverStep(hint.strategy, descriptions, hint.id);
                madeProgress = true;
            } else if (hint.answer !== undefined && hint.answer !== null) {
                this.addGuess(hint.id, hint.answer);
                this.recordSolverStep(hint.strategy, [
                    `Placed ${hint.answer} in cell ${hint.id + 1}`,
                ], hint.id);
                madeProgress = true;
            }
        }

        let usedBruteForce = false;
        let success = this.isComplete();

        if (!success) {
            usedBruteForce = this.solveBruteForce();
            if (usedBruteForce) {
                this.cells.forEach((cell) => {
                    if (!cell.answer) {
                        return;
                    }
                    if (cell.guess !== cell.answer) {
                        cell.addGuess(cell.answer);
                    }
                });
                this.recordSolverStep("bruteForce", ["__BRUTE_FORCE__"]);
                success = this.isComplete();
            }
        }

        this.completeSolverRun(usedBruteForce);

        return {
            success,
            usedBruteForce,
            steps: this.getSolverHistory(),
        };
    }

    replacePossibilitiesWithSolverPossibilities() {
        this.cells.forEach((cell) => {
            cell.replacePossibilitiesWithSolverPossibilities();
        })
    }

    resetPossibilitiesToBaseline(startingPosition: (string | number)[]) {
        this.calculateAllPossibilities();
        this.cells.forEach((cell, index) => {
            if (cell.guess) {
                cell.resetPossibilities();
                return;
            }

            const baseline = startingPosition[index]?.toString() ?? "";
            if (baseline.startsWith("P")) {
                const calculatedPossibilities = cell.solverPossibilites;
                cell.solverPossibilites = new Set(
                    baseline
                        .slice(1)
                        .split("")
                        .filter((candidate) =>
                            this.validInputs.has(candidate) &&
                            calculatedPossibilities.has(candidate)
                        )
                );
            }
            cell.replacePossibilitiesWithSolverPossibilities();
        });
    }

    /**
     * Iterate through the strategies until one of them is able to find the answer for a cell.
     * @returns
     */
    hint(allowedStrategies?: string[], allowSubsetPossibilities: boolean = false, promptOnSubset: boolean = false): Hint | null {
        const allowedStrategySet = allowedStrategies && allowedStrategies.length
            ? new Set(allowedStrategies)
            : this.allowedStrategyKeys;
        let guessStrategies = [
            this.strategyFullHouse,
            this.strategyNakedSingle,
            this.strategyHiddenSingle,
        ];
        let possibilityStrategies = [
            this.strategyFullHouse,
            this.strategyNakedSingle,
            this.strategyHiddenSingle,
            this.strategyLockedCandidatesType1,
            this.strategyLockedCandidatesType2,
            this.strategyNakedPair,
            this.strategyHiddenPair,
            this.strategyNakedTriple,
            this.strategyHiddenTriple,
            this.strategyNakedQuadruple,
            this.strategyHiddenQuadruple,
            this.strategyXWing,
            this.strategySwordfish,
            this.strategyJellyfish,
            this.strategyXYWing,
            this.strategyXYZWing,
            this.strategySkyscraper,
            this.strategyTwoStringKite,
            this.strategyFinnedXWing,
            // Preserve the established hint priority when multiple advanced patterns apply.
            this.strategyWWing,
        ];
        if (allowedStrategySet && allowedStrategySet.size > 0) {
            const isAllowed = (strategy: () => Hint | null) => allowedStrategySet.has(strategy.name);
            guessStrategies = guessStrategies.filter(isAllowed);
            possibilityStrategies = possibilityStrategies.filter(isAllowed);
        }

        if (this.hasErrors()) {
            // Custom strategy to show errors
            const errorCells = this.cells.filter(c => c.isWrong());
            // "No hints available when there are errors in the puzzle"
            return {
                id: errorCells[0].id,
                answer: errorCells[0].answer,
                strategy: "Error",
                description: () => {
                    return [
                        this.createUIUpdateFunction(
                            "No hints available when there are errors in the puzzle",
                            errorCells[0],
                            false,
                            errorCells,
                            [],
                            errorCells[0],
                            null
                        ),
                        () => {
                            this.addGuess(errorCells[0].id, errorCells[0].guess);
                            this.updateGameBoardUI(true);
                            return "Removed incorrect value";
                        },
                    ];
                }
            };
        }

        this.calculateAllPossibilities(true);
        let strategyResult: Hint | null = null;
        for (var i = 0; i < guessStrategies.length; i++) {
            strategyResult = guessStrategies[i].bind(this)();
            if (strategyResult) {
                return strategyResult
            }
        }

        const { invalidCells, subsetCells } = this.validateUserPossibilities(false, allowSubsetPossibilities);

        if (invalidCells.length > 0) {
            // Custom strategy to validate user possibilities
            return {
                id: invalidCells[0].id,
                answer: invalidCells[0].answer,
                strategy: "Error Possibilities",
                description: () => {
                    return [
                        this.createUIUpdateFunction(
                            "Possibilities in some cells are missing or different than calculated possibilities",
                            invalidCells[0],
                            false,
                            invalidCells,
                            [],
                            null,
                            null
                        ),
                        () => {
                            this.validateUserPossibilities(true, false);
                            this.updateGameBoardUI(true);
                            return "Updated to calculated possibilities";
                        },
                    ];
                }
            };
        }

        if (allowSubsetPossibilities && subsetCells.length > 0) {
            // Custom strategy to confirm using user possibilities as solver possibilities
            return {
                id: subsetCells[0].id,
                answer: subsetCells[0].answer,
                strategy: "Use User Possibilities",
                description: () => {
                    this.userPossibilitiesChoice = null;
                    const hintMessage = promptOnSubset
                        ? "possibilities in some cells are different than the calculated possibilities. Use your possibilities?<div style='margin-top:8px;'><button id='hint-use-yes' class='btn btn-sm btn-primary' style='margin-right:6px;'>Yes</button><button id='hint-use-no' class='btn btn-sm btn-secondary'>No</button></div>"
                        : "possibilities in some cells are different than the calculated possibilities.";
                    if (promptOnSubset) {
                        window.setTimeout(() => {
                            const $yes = $("#hint-use-yes");
                            const $no = $("#hint-use-no");
                            $yes.off("click").on("click", () => {
                                this.userPossibilitiesChoice = true;
                                $("#hint-right").trigger("click");
                            });
                            $no.off("click").on("click", () => {
                                this.userPossibilitiesChoice = false;
                                $("#hint-right").trigger("click");
                            });
                        }, 0);
                    }
                    return [
                        this.createUIUpdateFunction(
                            hintMessage,
                            subsetCells[0],
                            false,
                            subsetCells,
                            [],
                            null,
                            null
                        ),
                        () => {
                            if (this.userPossibilitiesChoice === true) {
                                this.applyUserPossibilitiesToSolver();
                                return "Using your possibilities";
                            }
                            this.replacePossibilitiesWithSolverPossibilities();
                            this.updateGameBoardUI(true);
                            return "Updated to calculated possibilities";
                        },
                    ];
                }
            };
        }

        for (var i = 0; i < possibilityStrategies.length; i++) {
            strategyResult = possibilityStrategies[i].bind(this)();
            if (strategyResult) {
                return strategyResult
            }
        }

        return null;
    }

    /**
     * Full House - When a unit (row, column, box) has 8 numbers filled,
     * the 9th cell must be the missing number
     */
    strategyFullHouse(): Hint | null {
        // Check rows
        for (let i = 0; i < this.size; i++) {
            const row = this.getRows()[i];
            const emptyCells = row.filter(cell => !cell.guess);
            if (emptyCells.length === 1) {
                const emptyCell = emptyCells[0];
                const usedNumbers = new Set(row.filter(cell => cell.guess).map(cell => cell.guess));
                const missingNumber = Array.from(this.validInputs).find(num => !usedNumbers.has(num));
                if (missingNumber) {
                    return {
                        id: emptyCell.id,
                        answer: missingNumber,
                        strategy: "strategyFullHouse",
                        description: this.descriptionFullHouse.bind(this)
                    };
                }
            }
        }

        // Check columns
        for (let i = 0; i < this.size; i++) {
            const column = this.getColumns()[i];
            const emptyCells = column.filter(cell => !cell.guess);
            if (emptyCells.length === 1) {
                const emptyCell = emptyCells[0];
                const usedNumbers = new Set(column.filter(cell => cell.guess).map(cell => cell.guess));
                const missingNumber = Array.from(this.validInputs).find(num => !usedNumbers.has(num));
                if (missingNumber) {
                    return {
                        id: emptyCell.id,
                        answer: missingNumber,
                        strategy: "strategyFullHouse",
                        description: this.descriptionFullHouse.bind(this)
                    };
                }
            }
        }

        // Check boxes
        for (let i = 0; i < this.size; i++) {
            const box = this.getBoxes()[i];
            const emptyCells = box.filter(cell => !cell.guess);
            if (emptyCells.length === 1) {
                const emptyCell = emptyCells[0];
                const usedNumbers = new Set(box.filter(cell => cell.guess).map(cell => cell.guess));
                const missingNumber = Array.from(this.validInputs).find(num => !usedNumbers.has(num));
                if (missingNumber) {
                    return {
                        id: emptyCell.id,
                        answer: missingNumber,
                        strategy: "strategyFullHouse",
                        description: this.descriptionFullHouse.bind(this)
                    };
                }
            }
        }

        return null;
    }

    /**
     * Naked Single - A cell with only one possible candidate
     */
    strategyNakedSingle(): Hint | null {
        for (let i = 0; i < this.cells.length; i++) {
            const cell = this.cells[i];
            if (!cell.guess && cell.solverPossibilites.size === 1) {
                return {
                    id: i,
                    answer: cell.solverPossibilites.values().next().value,
                    strategy: "strategyNakedSingle",
                    description: this.descriptionNakedSingle.bind(this)
                };
            }
        }
        return null;
    }

    /**
     * Hidden Single - A candidate that can only go in one cell within a unit
     */
    strategyHiddenSingle(): Hint | null {
        // Check each unit (row, column, box) for hidden singles
        const units = [
            ...this.getRows().map((row, i) => ({ type: 'row', index: i, cells: row })),
            ...this.getColumns().map((col, i) => ({ type: 'column', index: i, cells: col })),
            ...this.getBoxes().map((box, i) => ({ type: 'box', index: i, cells: box }))
        ];

        for (const unit of units) {
            for (const candidate of this.validInputs) {
                const candidateCells = unit.cells.filter(cell =>
                    !cell.guess && cell.solverPossibilites.has(candidate)
                );

                if (candidateCells.length === 1) {
                    const cell = candidateCells[0];
                    return {
                        id: cell.id,
                        answer: candidate,
                        strategy: "strategyHiddenSingle",
                        description: this.descriptionHiddenSingle.bind(this)
                    };
                }
            }
        }

        return null;
    }

    /**
     * Locked Candidates Type 1 (Pointing) - When all candidates for a number in a box
     * are confined to a single row or column, eliminate from that row/column outside the box
     */
    strategyLockedCandidatesType1(): Hint | null {
        for (let boxIndex = 0; boxIndex < this.size; boxIndex++) {
            const box = this.getBoxes()[boxIndex];

            for (const candidate of this.validInputs) {
                const candidateCells = box.filter(cell =>
                    !cell.guess && cell.solverPossibilites.has(candidate)
                );

                if (candidateCells.length >= 2 && candidateCells.length <= 3) {
                    // Check if all candidates are in the same row
                    const rows = new Set(candidateCells.map(cell => cell.row));
                    if (rows.size === 1) {
                        const row = candidateCells[0].row;
                        const rowCells = this.getCellsInSameRow(candidateCells[0].id, false);
                        const eliminationCells = rowCells.filter(cell =>
                            cell.box !== boxIndex &&
                            !cell.guess &&
                            cell.solverPossibilites.has(candidate)
                        );

                        if (eliminationCells.length > 0) {
                            return {
                                id: candidateCells[0].id,
                                answer: candidate,
                                strategy: "strategyLockedCandidatesType1",
                                description: this.descriptionLockedCandidatesType1.bind(this),
                                data: {
                                    boxIndex,
                                    candidate,
                                    candidateCellIds: candidateCells.map(c => c.id),
                                    eliminationCellIds: eliminationCells.map(c => c.id),
                                    unitType: 'row',
                                    unitIndex: row
                                }
                            };
                        }
                    }

                    // Check if all candidates are in the same column
                    const columns = new Set(candidateCells.map(cell => cell.column));
                    if (columns.size === 1) {
                        const column = candidateCells[0].column;
                        const columnCells = this.getCellsInSameColumn(candidateCells[0].id, false);
                        const eliminationCells = columnCells.filter(cell =>
                            cell.box !== boxIndex &&
                            !cell.guess &&
                            cell.solverPossibilites.has(candidate)
                        );

                        if (eliminationCells.length > 0) {
                            return {
                                id: candidateCells[0].id,
                                answer: candidate,
                                strategy: "strategyLockedCandidatesType1",
                                description: this.descriptionLockedCandidatesType1.bind(this),
                                data: {
                                    boxIndex,
                                    candidate,
                                    candidateCellIds: candidateCells.map(c => c.id),
                                    eliminationCellIds: eliminationCells.map(c => c.id),
                                    unitType: 'column',
                                    unitIndex: column
                                }
                            };
                        }
                    }
                }
            }
        }
        return null;
    }

    /**
     * Locked Candidates Type 2 (Claiming) - When all candidates for a number in a row/column
     * are confined to a single box, eliminate from that box outside the row/column
     */
    strategyLockedCandidatesType2(): Hint | null {
        // Check rows
        for (let rowIndex = 0; rowIndex < this.size; rowIndex++) {
            const row = this.getRows()[rowIndex];

            for (const candidate of this.validInputs) {
                const candidateCells = row.filter(cell =>
                    !cell.guess && cell.solverPossibilites.has(candidate)
                );

                if (candidateCells.length >= 2 && candidateCells.length <= 3) {
                    const boxes = new Set(candidateCells.map(cell => cell.box));
                    if (boxes.size === 1) {
                        const boxIndex = candidateCells[0].box;
                        const boxCells = this.getCellsInSameBox(candidateCells[0].id, false);
                        const eliminationCells = boxCells.filter(cell =>
                            cell.row !== rowIndex &&
                            !cell.guess &&
                            cell.solverPossibilites.has(candidate)
                        );

                        if (eliminationCells.length > 0) {
                            return {
                                id: candidateCells[0].id,
                                answer: candidate,
                                strategy: "strategyLockedCandidatesType2",
                                description: this.descriptionLockedCandidatesType2.bind(this),
                                data: {
                                    unitType: 'row',
                                    unitIndex: rowIndex,
                                    boxIndex,
                                    candidate,
                                    candidateCellIds: candidateCells.map(c => c.id),
                                    eliminationCellIds: eliminationCells.map(c => c.id)
                                }
                            };
                        }
                    }
                }
            }
        }

        // Check columns
        for (let columnIndex = 0; columnIndex < this.size; columnIndex++) {
            const column = this.getColumns()[columnIndex];

            for (const candidate of this.validInputs) {
                const candidateCells = column.filter(cell =>
                    !cell.guess && cell.solverPossibilites.has(candidate)
                );

                if (candidateCells.length >= 2 && candidateCells.length <= 3) {
                    const boxes = new Set(candidateCells.map(cell => cell.box));
                    if (boxes.size === 1) {
                        const boxIndex = candidateCells[0].box;
                        const boxCells = this.getCellsInSameBox(candidateCells[0].id, false);
                        const eliminationCells = boxCells.filter(cell =>
                            cell.column !== columnIndex &&
                            !cell.guess &&
                            cell.solverPossibilites.has(candidate)
                        );

                        if (eliminationCells.length > 0) {
                            return {
                                id: candidateCells[0].id,
                                answer: candidate,
                                strategy: "strategyLockedCandidatesType2",
                                description: this.descriptionLockedCandidatesType2.bind(this),
                                data: {
                                    unitType: 'column',
                                    unitIndex: columnIndex,
                                    boxIndex,
                                    candidate,
                                    candidateCellIds: candidateCells.map(c => c.id),
                                    eliminationCellIds: eliminationCells.map(c => c.id)
                                }
                            };
                        }
                    }
                }
            }
        }
        return null;
    }

    /**
     * Naked Pair - Two cells in a unit with exactly the same two candidates
     */
    strategyNakedPair(): Hint | null {
        const units = [
            ...this.getRows().map((row, i) => ({ type: 'row', index: i, cells: row })),
            ...this.getColumns().map((col, i) => ({ type: 'column', index: i, cells: col })),
            ...this.getBoxes().map((box, i) => ({ type: 'box', index: i, cells: box }))
        ];

        for (const unit of units) {
            const candidateCells = unit.cells.filter(cell =>
                !cell.guess && cell.solverPossibilites.size === 2
            );

            for (let i = 0; i < candidateCells.length - 1; i++) {
                for (let j = i + 1; j < candidateCells.length; j++) {
                    const cell1 = candidateCells[i];
                    const cell2 = candidateCells[j];

                    // Check if they have the same candidates
                    if (this.setsEqual(cell1.solverPossibilites, cell2.solverPossibilites)) {
                        const candidates = Array.from(cell1.solverPossibilites);

                        // Check if we can eliminate these candidates from other cells
                        const eliminationCells = unit.cells.filter(cell =>
                            cell !== cell1 && cell !== cell2 && !cell.guess &&
                            (cell.solverPossibilites.has(candidates[0]) || cell.solverPossibilites.has(candidates[1]))
                        );

                        if (eliminationCells.length > 0) {
                            return {
                                id: cell1.id,
                                answer: candidates[0], // For elimination, not placement
                                strategy: "strategyNakedPair",
                                description: this.descriptionNakedPair.bind(this),
                                data: {
                                    cellIds: [cell1.id, cell2.id],
                                    candidates,
                                    unitType: unit.type,
                                    eliminationCellIds: eliminationCells.map(c => c.id)
                                }
                            };
                        }
                    }
                }
            }
        }
        return null;
    }

    /**
     * Hidden Pair - Two candidates that can only appear in two cells within a unit
     */
    strategyHiddenPair(): Hint | null {
        const units = [
            ...this.getRows().map((row, i) => ({ type: 'row', index: i, cells: row })),
            ...this.getColumns().map((col, i) => ({ type: 'column', index: i, cells: col })),
            ...this.getBoxes().map((box, i) => ({ type: 'box', index: i, cells: box }))
        ];

        for (const unit of units) {
            const candidates = Array.from(this.validInputs);

            for (let i = 0; i < candidates.length - 1; i++) {
                for (let j = i + 1; j < candidates.length; j++) {
                    const cand1 = candidates[i];
                    const cand2 = candidates[j];

                    const cells1 = unit.cells.filter(cell =>
                        !cell.guess && cell.solverPossibilites.has(cand1)
                    );
                    const cells2 = unit.cells.filter(cell =>
                        !cell.guess && cell.solverPossibilites.has(cand2)
                    );

                    // Check if both candidates appear in exactly the same two cells
                    if (cells1.length === 2 && cells2.length === 2 &&
                        cells1[0] === cells2[0] && cells1[1] === cells2[1]) {

                        // Check if these cells have other candidates that can be eliminated
                        const eliminationCandidates = new Set<string>();
                        for (const cell of cells1) {
                            for (const candidate of cell.solverPossibilites) {
                                if (![cand1, cand2].includes(candidate)) {
                                    eliminationCandidates.add(candidate);
                                }
                            }
                        }

                        if (eliminationCandidates.size > 0) {
                            return {
                                id: cells1[0].id,
                                answer: cand1,
                                strategy: "strategyHiddenPair",
                                description: this.descriptionHiddenPair.bind(this),
                                data: {
                                    candidates: [cand1, cand2],
                                    cellIds: cells1.map(cell => cell.id),
                                    unitType: unit.type,
                                    unitIndex: unit.index,
                                    eliminationCandidates: Array.from(eliminationCandidates)
                                }
                            };
                        }
                    }
                }
            }
        }
        return null;
    }

    /**
     * Naked Triple - Three cells in a unit containing exactly the same three candidates
     */
    strategyNakedTriple(): Hint | null {
        const units = [
            ...this.getRows().map((row, i) => ({ type: 'row', index: i, cells: row })),
            ...this.getColumns().map((col, i) => ({ type: 'column', index: i, cells: col })),
            ...this.getBoxes().map((box, i) => ({ type: 'box', index: i, cells: box }))
        ];

        for (const unit of units) {
            const candidateCells = unit.cells.filter(cell =>
                !cell.guess && cell.solverPossibilites.size >= 2 && cell.solverPossibilites.size <= 3
            );

            // Check all combinations of 3 cells
            for (let i = 0; i < candidateCells.length - 2; i++) {
                for (let j = i + 1; j < candidateCells.length - 1; j++) {
                    for (let k = j + 1; k < candidateCells.length; k++) {
                        const cell1 = candidateCells[i];
                        const cell2 = candidateCells[j];
                        const cell3 = candidateCells[k];

                        // Combine all candidates from the three cells
                        const combinedCandidates = new Set([
                            ...cell1.solverPossibilites,
                            ...cell2.solverPossibilites,
                            ...cell3.solverPossibilites
                        ]);

                        // Check if they form a naked triple (exactly 3 candidates total)
                        if (combinedCandidates.size === 3) {
                            const candidates = Array.from(combinedCandidates);

                            // Check if we can eliminate these candidates from other cells
                            const eliminationCells = unit.cells.filter(cell =>
                                ![cell1, cell2, cell3].includes(cell) && !cell.guess &&
                                candidates.some(cand => cell.solverPossibilites.has(cand))
                            );

                            if (eliminationCells.length > 0) {
                                return {
                                    id: cell1.id,
                                    answer: candidates[0],
                                    strategy: "strategyNakedTriple",
                                    description: this.descriptionNakedTriple.bind(this),
                                    data: {
                                        cellIds: [cell1.id, cell2.id, cell3.id],
                                        candidates,
                                        unitType: unit.type,
                                        unitIndex: unit.index,
                                        eliminationCellIds: eliminationCells.map(cell => cell.id)
                                    }
                                };
                            }
                        }
                    }
                }
            }
        }
        return null;
    }

    /**
     * Hidden Triple - Three candidates that can only appear in three specific cells within a unit
     */
    strategyHiddenTriple(): Hint | null {
        const units = [
            ...this.getRows().map((row, i) => ({ type: 'row', index: i, cells: row })),
            ...this.getColumns().map((col, i) => ({ type: 'column', index: i, cells: col })),
            ...this.getBoxes().map((box, i) => ({ type: 'box', index: i, cells: box }))
        ];

        for (const unit of units) {
            const emptyCells = unit.cells.filter(cell => !cell.guess);

            // Need at least 4 empty cells for a hidden triple to be meaningful
            if (emptyCells.length <= 3) {
                continue;
            }

            const candidates = Array.from(this.validInputs);

            // Check all combinations of 3 candidates
            for (let i = 0; i < candidates.length - 2; i++) {
                for (let j = i + 1; j < candidates.length - 1; j++) {
                    for (let k = j + 1; k < candidates.length; k++) {
                        const cand1 = candidates[i];
                        const cand2 = candidates[j];
                        const cand3 = candidates[k];

                        // Find cells that contain each candidate
                        const cells1 = emptyCells.filter(cell => cell.solverPossibilites.has(cand1));
                        const cells2 = emptyCells.filter(cell => cell.solverPossibilites.has(cand2));
                        const cells3 = emptyCells.filter(cell => cell.solverPossibilites.has(cand3));

                        // Each candidate must appear in 2-3 cells (not just 1, not more than 3)
                        if (cells1.length < 2 || cells1.length > 3 ||
                            cells2.length < 2 || cells2.length > 3 ||
                            cells3.length < 2 || cells3.length > 3) {
                            continue;
                        }

                        // Find the union of all cells that contain any of the three candidates
                        const allCells = new Set([...cells1, ...cells2, ...cells3]);

                        // Hidden Triple: exactly 3 cells contain these candidates AND no other cells in the unit contain them
                        if (allCells.size === 3) {
                            const tripleCells = Array.from(allCells);

                            // Verify this is actually a hidden triple by checking that these candidates
                            // don't appear in any other cells in this unit
                            const otherCells = emptyCells.filter(cell => !allCells.has(cell));
                            const isValidHiddenTriple = !otherCells.some(cell =>
                                cell.solverPossibilites.has(cand1) ||
                                cell.solverPossibilites.has(cand2) ||
                                cell.solverPossibilites.has(cand3)
                            );

                            if (!isValidHiddenTriple) {
                                continue;
                            }

                            // Check if these cells have other candidates that can be eliminated
                            const eliminationCandidates = new Set<string>();
                            for (const cell of tripleCells) {
                                for (const candidate of cell.solverPossibilites) {
                                    if (![cand1, cand2, cand3].includes(candidate)) {
                                        eliminationCandidates.add(candidate);
                                    }
                                }
                            }

                            if (eliminationCandidates.size > 0) {
                                return {
                                    id: tripleCells[0].id,
                                    answer: Array.from(eliminationCandidates)[0], // First elimination candidate for display
                                    strategy: "strategyHiddenTriple",
                                    description: this.descriptionHiddenTriple.bind(this),
                                    data: {
                                        candidates: [cand1, cand2, cand3],
                                        cellIds: tripleCells.map(cell => cell.id),
                                        unitType: unit.type,
                                        eliminationCandidates: Array.from(eliminationCandidates)
                                    }
                                };
                            }
                        }
                    }
                }
            }
        }
        return null;
    }

    /**
     * Naked Quadruple - Four cells in a unit containing exactly the same four candidates
     */
    strategyNakedQuadruple(): Hint | null {
        const units = [
            ...this.getRows().map((row, i) => ({ type: 'row', index: i, cells: row })),
            ...this.getColumns().map((col, i) => ({ type: 'column', index: i, cells: col })),
            ...this.getBoxes().map((box, i) => ({ type: 'box', index: i, cells: box }))
        ];

        for (const unit of units) {
            const candidateCells = unit.cells.filter(cell =>
                !cell.guess && cell.solverPossibilites.size >= 2 && cell.solverPossibilites.size <= 4
            );

            // Check all combinations of 4 cells
            for (let i = 0; i < candidateCells.length - 3; i++) {
                for (let j = i + 1; j < candidateCells.length - 2; j++) {
                    for (let k = j + 1; k < candidateCells.length - 1; k++) {
                        for (let l = k + 1; l < candidateCells.length; l++) {
                            const cells = [candidateCells[i], candidateCells[j], candidateCells[k], candidateCells[l]];

                            // Combine all candidates from the four cells
                            const combinedCandidates = new Set<string>();
                            cells.forEach(cell => {
                                cell.solverPossibilites.forEach(cand => combinedCandidates.add(cand));
                            });

                            // Check if they form a naked quadruple (exactly 4 candidates total)
                            if (combinedCandidates.size === 4) {
                                const candidates = Array.from(combinedCandidates);

                                // Check if we can eliminate these candidates from other cells
                                const eliminationCells = unit.cells.filter(cell =>
                                    !cells.includes(cell) && !cell.guess &&
                                    candidates.some(cand => cell.solverPossibilites.has(cand))
                                );

                                if (eliminationCells.length > 0) {
                                    return {
                                        id: cells[0].id,
                                        answer: candidates[0],
                                        strategy: "strategyNakedQuadruple",
                                        description: this.descriptionNakedQuadruple.bind(this),
                                        data: {
                                            cellIds: cells.map(c => c.id),
                                            candidates,
                                            unitType: unit.type,
                                            eliminationCellIds: eliminationCells.map(c => c.id)
                                        }
                                    };
                                }
                            }
                        }
                    }
                }
            }
        }
        return null;
    }

    /**
     * Hidden Quadruple - Four candidates that can only appear in four specific cells within a unit
     */
    strategyHiddenQuadruple(): Hint | null {
        const units = [
            ...this.getRows().map((row, i) => ({ type: 'row', index: i, cells: row })),
            ...this.getColumns().map((col, i) => ({ type: 'column', index: i, cells: col })),
            ...this.getBoxes().map((box, i) => ({ type: 'box', index: i, cells: box }))
        ];

        for (const unit of units) {
            const emptyCells = unit.cells.filter(cell => !cell.guess);

            // Need at least 5 empty cells for a hidden quadruple to be meaningful
            if (emptyCells.length <= 4) {
                continue;
            }

            const candidates = Array.from(this.validInputs);

            // Check all combinations of 4 candidates
            for (let i = 0; i < candidates.length - 3; i++) {
                for (let j = i + 1; j < candidates.length - 2; j++) {
                    for (let k = j + 1; k < candidates.length - 1; k++) {
                        for (let l = k + 1; l < candidates.length; l++) {
                            const quadCandidates = [candidates[i], candidates[j], candidates[k], candidates[l]];

                            // For each candidate, find cells in this unit that contain it
                            const candidateCellSets: Cell[][] = [];
                            let validQuadruple = true;

                            for (const candidate of quadCandidates) {
                                const cellsWithCandidate = emptyCells.filter(cell =>
                                    cell.solverPossibilites.has(candidate)
                                );

                                // Each candidate must appear in 2-4 cells (not 1, not more than 4)
                                if (cellsWithCandidate.length < 2 || cellsWithCandidate.length > 4) {
                                    validQuadruple = false;
                                    break;
                                }
                                candidateCellSets.push(cellsWithCandidate);
                            }

                            if (!validQuadruple) {
                                continue;
                            }

                            // Find the union of all cells that contain any of these candidates
                            const allCells = new Set<Cell>();
                            candidateCellSets.forEach(cellSet => {
                                cellSet.forEach(cell => allCells.add(cell));
                            });

                            // Hidden Quadruple: exactly 4 cells contain these candidates AND
                            // these candidates don't appear in any other cells in this unit
                            if (allCells.size === 4) {
                                const quadCells = Array.from(allCells);

                                // Verify this is actually a hidden quadruple by checking that these candidates
                                // don't appear in any other cells in this unit
                                const otherCells = emptyCells.filter(cell => !allCells.has(cell));
                                const isValidHiddenQuadruple = !otherCells.some(cell =>
                                    quadCandidates.some(candidate => cell.solverPossibilites.has(candidate))
                                );

                                if (!isValidHiddenQuadruple) {
                                    continue;
                                }

                                // Check if these cells have other candidates that can be eliminated
                                const eliminationCandidates = new Set<string>();
                                for (const cell of quadCells) {
                                    for (const candidate of cell.solverPossibilites) {
                                        if (!quadCandidates.includes(candidate)) {
                                            eliminationCandidates.add(candidate);
                                        }
                                    }
                                }

                                if (eliminationCandidates.size > 0) {
                                    return {
                                        id: quadCells[0].id,
                                        answer: quadCandidates[0],
                                        strategy: "strategyHiddenQuadruple",
                                        description: this.descriptionHiddenQuadruple.bind(this),
                                        data: {
                                            cellIds: quadCells.map(c => c.id),
                                            candidates: quadCandidates,
                                            unitType: unit.type,
                                            eliminationCandidates: Array.from(eliminationCandidates)
                                        }
                                    };
                                }
                            }
                        }
                    }
                }
            }
        }
        return null;
    }

    /**
     * X-Wing - A fish pattern where a candidate appears in exactly 2 rows and 2 columns
     */
    strategyXWing(): Hint | null {
        for (const candidate of this.validInputs) {
            // Check for X-Wing in rows
            const rowsWithCandidate: number[][] = [];
            for (let r = 0; r < this.size; r++) {
                const row = this.getRows()[r];
                const candidateCells = row.filter(cell =>
                    !cell.guess && cell.solverPossibilites.has(candidate)
                );
                if (candidateCells.length === 2) {
                    rowsWithCandidate.push([r, candidateCells[0].column, candidateCells[1].column]);
                }
            }

            // Check if any two rows form an X-Wing
            for (let i = 0; i < rowsWithCandidate.length - 1; i++) {
                for (let j = i + 1; j < rowsWithCandidate.length; j++) {
                    const [row1, col1a, col1b] = rowsWithCandidate[i];
                    const [row2, col2a, col2b] = rowsWithCandidate[j];

                    // Check if they share the same columns
                    if ((col1a === col2a && col1b === col2b) || (col1a === col2b && col1b === col2a)) {
                        const columns = [col1a, col1b].sort();

                        // Ensure the X-Wing cells are in different boxes (optional validation)
                        const cell1 = this.cells[row1 * this.size + columns[0]];
                        const cell2 = this.cells[row1 * this.size + columns[1]];
                        const cell3 = this.cells[row2 * this.size + columns[0]];
                        const cell4 = this.cells[row2 * this.size + columns[1]];
                        const boxes = new Set([cell1.box, cell2.box, cell3.box, cell4.box]);

                        // X-Wing should typically span multiple boxes (though not strictly required)
                        if (boxes.size < 2) continue;

                        // Check if we can eliminate candidates from these columns
                        let eliminationCells: Cell[] = [];
                        for (const col of columns) {
                            const columnCells = this.getColumns()[col];
                            eliminationCells.push(...columnCells.filter(cell =>
                                cell.row !== row1 && cell.row !== row2 && !cell.guess &&
                                cell.solverPossibilites.has(candidate)
                            ));
                        }

                        if (eliminationCells.length > 0) {
                            const patternCells = [
                                this.cells[row1 * this.size + columns[0]],
                                this.cells[row1 * this.size + columns[1]],
                                this.cells[row2 * this.size + columns[0]],
                                this.cells[row2 * this.size + columns[1]]
                            ];
                            return {
                                id: this.cells[row1 * this.size + columns[0]].id,
                                answer: candidate,
                                strategy: "strategyXWing",
                                description: this.descriptionXWing.bind(this),
                                data: {
                                    candidates: [candidate],
                                    patternRows: [row1, row2],
                                    patternCols: columns,
                                    cellIds: patternCells.map(cell => cell.id),
                                    eliminationCellIds: eliminationCells.map(cell => cell.id),
                                    patternType: 'row-based'
                                }
                            };
                        }
                    }
                }
            }

            // Check for X-Wing in columns (similar logic but transposed)
            const colsWithCandidate: number[][] = [];
            for (let c = 0; c < this.size; c++) {
                const column = this.getColumns()[c];
                const candidateCells = column.filter(cell =>
                    !cell.guess && cell.solverPossibilites.has(candidate)
                );
                if (candidateCells.length === 2) {
                    colsWithCandidate.push([c, candidateCells[0].row, candidateCells[1].row]);
                }
            }

            // Check if any two columns form an X-Wing
            for (let i = 0; i < colsWithCandidate.length - 1; i++) {
                for (let j = i + 1; j < colsWithCandidate.length; j++) {
                    const [col1, row1a, row1b] = colsWithCandidate[i];
                    const [col2, row2a, row2b] = colsWithCandidate[j];

                    if ((row1a === row2a && row1b === row2b) || (row1a === row2b && row1b === row2a)) {
                        const rows = [row1a, row1b].sort();

                        // Ensure the X-Wing cells are in different boxes (optional validation)
                        const cell1 = this.cells[rows[0] * this.size + col1];
                        const cell2 = this.cells[rows[1] * this.size + col1];
                        const cell3 = this.cells[rows[0] * this.size + col2];
                        const cell4 = this.cells[rows[1] * this.size + col2];
                        const boxes = new Set([cell1.box, cell2.box, cell3.box, cell4.box]);

                        // X-Wing should typically span multiple boxes (though not strictly required)
                        if (boxes.size < 2) continue;

                        let eliminationCells: Cell[] = [];
                        for (const row of rows) {
                            const rowCells = this.getRows()[row];
                            eliminationCells.push(...rowCells.filter(cell =>
                                cell.column !== col1 && cell.column !== col2 && !cell.guess &&
                                cell.solverPossibilites.has(candidate)
                            ));
                        }

                        if (eliminationCells.length > 0) {
                            const patternCells = [
                                this.cells[rows[0] * this.size + col1],
                                this.cells[rows[1] * this.size + col1],
                                this.cells[rows[0] * this.size + col2],
                                this.cells[rows[1] * this.size + col2]
                            ];
                            return {
                                id: this.cells[rows[0] * this.size + col1].id,
                                answer: candidate,
                                strategy: "strategyXWing",
                                description: this.descriptionXWing.bind(this),
                                data: {
                                    candidates: [candidate],
                                    patternRows: rows,
                                    patternCols: [col1, col2],
                                    cellIds: patternCells.map(cell => cell.id),
                                    eliminationCellIds: eliminationCells.map(cell => cell.id),
                                    patternType: 'column-based'
                                }
                            };
                        }
                    }
                }
            }
        }
        return null;
    }

    /**
     * Finned X-Wing - an X-Wing with an extra candidate (fin) in one box
     */
    strategyFinnedXWing(): Hint | null {
        for (const candidate of this.validInputs) {
            // Row-based search: find rows with the candidate to identify a base row plus a fin row.
            const rowsWithCandidate: Array<{ row: number; cols: number[] }> = [];
            for (let r = 0; r < this.size; r++) {
                const row = this.getRows()[r];
                const candidateCells = row.filter(cell =>
                    !cell.guess && cell.solverPossibilites.has(candidate)
                );
                if (candidateCells.length >= 2) {
                    rowsWithCandidate.push({
                        row: r,
                        cols: candidateCells.map(cell => cell.column),
                    });
                }
            }
            for (let i = 0; i < rowsWithCandidate.length - 1; i++) {
                for (let j = i + 1; j < rowsWithCandidate.length; j++) {
                    const rowA = rowsWithCandidate[i];
                    const rowB = rowsWithCandidate[j];
                    // Base row must have exactly 2 positions for the candidate (a clean X-Wing base).
                    const baseRow = rowA.cols.length === 2 ? rowA : (rowB.cols.length === 2 ? rowB : null);
                    const finRow = baseRow === rowA ? rowB : (baseRow === rowB ? rowA : null);
                    if (!baseRow || !finRow) {
                        continue;
                    }

                    const baseCols = baseRow.cols.slice().sort();
                    // The fin row must include the base columns (plus at least one extra fin column).
                    if (!baseCols.every(col => finRow.cols.includes(col))) {
                        continue;
                    }

                    const finCols = finRow.cols.filter(col => !baseCols.includes(col));
                    if (!finCols.length) {
                        continue;
                    }

                    for (const baseCol of baseCols) {
                        const baseCell = this.cells[finRow.row * this.size + baseCol];
                        const finBox = baseCell.box;
                        // All fin candidates must sit in the same box as the fin-row base cell.
                        const finInBox = finCols.filter(col => this.cells[finRow.row * this.size + col].box === finBox);
                        if (finInBox.length === 0 || finInBox.length !== finCols.length) {
                            continue;
                        }

                        const finCells = finInBox.map(col => this.cells[finRow.row * this.size + col]);
                        const finCell = finCells[0];
                        // Eliminations are in the base column within the fin box, excluding the base rows.
                        const eliminationCells = this.getColumns()[baseCol].filter(cell =>
                            cell.box === finBox &&
                            cell.row !== baseRow.row &&
                            cell.row !== finRow.row &&
                            cell.id !== baseCell.id &&
                            cell.id !== finCell.id &&
                            !cell.guess &&
                            cell.solverPossibilites.has(candidate)
                        );

                        if (eliminationCells.length > 0) {
                            const baseCellIds = [
                                this.cells[baseRow.row * this.size + baseCols[0]].id,
                                this.cells[baseRow.row * this.size + baseCols[1]].id,
                                this.cells[finRow.row * this.size + baseCols[0]].id,
                                this.cells[finRow.row * this.size + baseCols[1]].id,
                            ];
                            return {
                                id: finCell.id,
                                answer: candidate,
                                strategy: "strategyFinnedXWing",
                                description: this.descriptionFinnedXWing.bind(this),
                                data: {
                                    candidates: [candidate],
                                    baseCellIds,
                                    finCellIds: finCells.map(cell => cell.id),
                                    eliminationCellIds: eliminationCells.map(cell => cell.id),
                                    patternType: "row-based",
                                }
                            };
                        }
                    }
                }
            }

            const colsWithCandidate: Array<{ col: number; rows: number[] }> = [];
            for (let c = 0; c < this.size; c++) {
                const column = this.getColumns()[c];
                const candidateCells = column.filter(cell =>
                    !cell.guess && cell.solverPossibilites.has(candidate)
                );
                if (candidateCells.length >= 2) {
                    colsWithCandidate.push({
                        col: c,
                        rows: candidateCells.map(cell => cell.row),
                    });
                }
            }

            for (let i = 0; i < colsWithCandidate.length - 1; i++) {
                for (let j = i + 1; j < colsWithCandidate.length; j++) {
                    const colA = colsWithCandidate[i];
                    const colB = colsWithCandidate[j];
                    // Base column must have exactly 2 positions for the candidate.
                    const baseCol = colA.rows.length === 2 ? colA : (colB.rows.length === 2 ? colB : null);
                    const finCol = baseCol === colA ? colB : (baseCol === colB ? colA : null);
                    if (!baseCol || !finCol) {
                        continue;
                    }

                    const baseRows = baseCol.rows.slice().sort();
                    // The fin column must include the base rows (plus at least one extra fin row).
                    if (!baseRows.every(row => finCol.rows.includes(row))) {
                        continue;
                    }

                    const finRows = finCol.rows.filter(row => !baseRows.includes(row));
                    if (!finRows.length) {
                        continue;
                    }

                    for (const baseRow of baseRows) {
                        const baseCell = this.cells[baseRow * this.size + finCol.col];
                        const finBox = baseCell.box;
                        // All fin candidates must sit in the same box as the fin-column base cell.
                        const finInBox = finRows.filter(row => this.cells[row * this.size + finCol.col].box === finBox);
                        if (finInBox.length === 0 || finInBox.length !== finRows.length) {
                            continue;
                        }

                        const finCells = finInBox.map(row => this.cells[row * this.size + finCol.col]);
                        const finCell = finCells[0];
                        // Eliminations are in the base row within the fin box, excluding the base columns.
                        const eliminationCells = this.getRows()[baseRow].filter(cell =>
                            cell.box === finBox &&
                            cell.column !== baseCol.col &&
                            cell.column !== finCol.col &&
                            cell.id !== baseCell.id &&
                            cell.id !== finCell.id &&
                            !cell.guess &&
                            cell.solverPossibilites.has(candidate)
                        );

                        if (eliminationCells.length > 0) {
                            const baseCellIds = [
                                this.cells[baseRows[0] * this.size + baseCol.col].id,
                                this.cells[baseRows[1] * this.size + baseCol.col].id,
                                this.cells[baseRows[0] * this.size + finCol.col].id,
                                this.cells[baseRows[1] * this.size + finCol.col].id,
                            ];
                            return {
                                id: finCell.id,
                                answer: candidate,
                                strategy: "strategyFinnedXWing",
                                description: this.descriptionFinnedXWing.bind(this),
                                data: {
                                    candidates: [candidate],
                                    baseCellIds,
                                    finCellIds: finCells.map(cell => cell.id),
                                    eliminationCellIds: eliminationCells.map(cell => cell.id),
                                    patternType: "column-based",
                                }
                            };
                        }
                    }
                }
            }
        }
        return null;
    }

    private findFish(size: number, strategyKey: string): Hint | null {
        const getCombinations = <T>(arr: T[], comboSize: number): T[][] => {
            const results: T[][] = [];
            const backtrack = (start: number, path: T[]) => {
                if (path.length === comboSize) {
                    results.push(path.slice());
                    return;
                }
                for (let i = start; i < arr.length; i++) {
                    path.push(arr[i]);
                    backtrack(i + 1, path);
                    path.pop();
                }
            };
            backtrack(0, []);
            return results;
        };

        for (const candidate of this.validInputs) {
            const rowsWithCandidate: Array<{ row: number; cols: number[] }> = [];
            for (let r = 0; r < this.size; r++) {
                const row = this.getRows()[r];
                const candidateCells = row.filter(cell =>
                    !cell.guess && cell.solverPossibilites.has(candidate)
                );
                if (candidateCells.length >= 2 && candidateCells.length <= size) {
                    rowsWithCandidate.push({
                        row: r,
                        cols: candidateCells.map(cell => cell.column),
                    });
                }
            }

            if (rowsWithCandidate.length >= size) {
                const combos = getCombinations(rowsWithCandidate, size);
                for (const combo of combos) {
                    const comboRows = combo.map(item => item.row);
                    const colSet = new Set<number>();
                    combo.forEach(item => item.cols.forEach(col => colSet.add(col)));
                    if (colSet.size !== size) {
                        continue;
                    }
                    const cols = Array.from(colSet);
                    const eliminationCells: Cell[] = [];
                    for (const col of cols) {
                        const columnCells = this.getColumns()[col];
                        eliminationCells.push(...columnCells.filter(cell =>
                            !comboRows.includes(cell.row) &&
                            !cell.guess &&
                            cell.solverPossibilites.has(candidate)
                        ));
                    }

                    if (eliminationCells.length > 0) {
                        const patternCells: Cell[] = [];
                        comboRows.forEach((row) => {
                            cols.forEach((col) => {
                                const cell = this.cells[row * this.size + col];
                                if (!cell.guess && cell.solverPossibilites.has(candidate)) {
                                    patternCells.push(cell);
                                }
                            });
                        });

                        const idCell = patternCells[0] ?? this.cells[comboRows[0] * this.size + cols[0]];
                        return {
                            id: idCell.id,
                            answer: candidate,
                            strategy: strategyKey,
                            description: strategyKey === "strategySwordfish"
                                ? this.descriptionSwordfish.bind(this)
                                : this.descriptionJellyfish.bind(this),
                            data: {
                                candidates: [candidate],
                                patternRows: comboRows,
                                patternCols: cols,
                                cellIds: patternCells.map(cell => cell.id),
                                eliminationCellIds: eliminationCells.map(cell => cell.id),
                                patternType: 'row-based',
                                fishSize: size,
                            }
                        };
                    }
                }
            }

            const colsWithCandidate: Array<{ col: number; rows: number[] }> = [];
            for (let c = 0; c < this.size; c++) {
                const column = this.getColumns()[c];
                const candidateCells = column.filter(cell =>
                    !cell.guess && cell.solverPossibilites.has(candidate)
                );
                if (candidateCells.length >= 2 && candidateCells.length <= size) {
                    colsWithCandidate.push({
                        col: c,
                        rows: candidateCells.map(cell => cell.row),
                    });
                }
            }

            if (colsWithCandidate.length >= size) {
                const combos = getCombinations(colsWithCandidate, size);
                for (const combo of combos) {
                    const comboCols = combo.map(item => item.col);
                    const rowSet = new Set<number>();
                    combo.forEach(item => item.rows.forEach(row => rowSet.add(row)));
                    if (rowSet.size !== size) {
                        continue;
                    }
                    const rows = Array.from(rowSet);
                    const eliminationCells: Cell[] = [];
                    for (const row of rows) {
                        const rowCells = this.getRows()[row];
                        eliminationCells.push(...rowCells.filter(cell =>
                            !comboCols.includes(cell.column) &&
                            !cell.guess &&
                            cell.solverPossibilites.has(candidate)
                        ));
                    }

                    if (eliminationCells.length > 0) {
                        const patternCells: Cell[] = [];
                        rows.forEach((row) => {
                            comboCols.forEach((col) => {
                                const cell = this.cells[row * this.size + col];
                                if (!cell.guess && cell.solverPossibilites.has(candidate)) {
                                    patternCells.push(cell);
                                }
                            });
                        });

                        const idCell = patternCells[0] ?? this.cells[rows[0] * this.size + comboCols[0]];
                        return {
                            id: idCell.id,
                            answer: candidate,
                            strategy: strategyKey,
                            description: strategyKey === "strategySwordfish"
                                ? this.descriptionSwordfish.bind(this)
                                : this.descriptionJellyfish.bind(this),
                            data: {
                                candidates: [candidate],
                                patternRows: rows,
                                patternCols: comboCols,
                                cellIds: patternCells.map(cell => cell.id),
                                eliminationCellIds: eliminationCells.map(cell => cell.id),
                                patternType: 'column-based',
                                fishSize: size,
                            }
                        };
                    }
                }
            }
        }

        return null;
    }

    strategySwordfish(): Hint | null {
        return this.findFish(3, "strategySwordfish");
    }

    strategyJellyfish(): Hint | null {
        return this.findFish(4, "strategyJellyfish");
    }

    /**
     * Skyscraper - a near fish pattern with two towers and eliminations in shared peers
     */
    strategySkyscraper(): Hint | null {
        for (const candidate of this.validInputs) {
            const rowsWithCandidate: Array<{ row: number; cols: number[]; cells: Cell[]; }> = [];
            for (let r = 0; r < this.size; r++) {
                const row = this.getRows()[r];
                const candidateCells = row.filter(cell =>
                    !cell.guess && cell.solverPossibilites.has(candidate)
                );
                if (candidateCells.length === 2) {
                    rowsWithCandidate.push({
                        row: r,
                        cols: [candidateCells[0].column, candidateCells[1].column],
                        cells: candidateCells
                    });
                }
            }

            for (let i = 0; i < rowsWithCandidate.length - 1; i++) {
                for (let j = i + 1; j < rowsWithCandidate.length; j++) {
                    const rowA = rowsWithCandidate[i];
                    const rowB = rowsWithCandidate[j];
                    const sharedCols = rowA.cols.filter(col => rowB.cols.includes(col));
                    if (sharedCols.length !== 1) {
                        continue;
                    }
                    const sharedCol = sharedCols[0];
                    const towerColA = rowA.cols.find(col => col !== sharedCol);
                    const towerColB = rowB.cols.find(col => col !== sharedCol);
                    if (towerColA === undefined || towerColB === undefined || towerColA === towerColB) {
                        continue;
                    }

                    const towerCellA = rowA.cells.find(cell => cell.column === towerColA);
                    const towerCellB = rowB.cells.find(cell => cell.column === towerColB);
                    if (!towerCellA || !towerCellB) {
                        continue;
                    }

                    const peersA = this.getAllRelatedCells(towerCellA.id, true);
                    const peersB = this.getAllRelatedCells(towerCellB.id, true);
                    const peerSetA = new Set(peersA.map(cell => cell.id));
                    const eliminationCells = peersB.filter(cell =>
                        peerSetA.has(cell.id) &&
                        !cell.guess &&
                        cell.solverPossibilites.has(candidate)
                    );

                    if (eliminationCells.length > 0) {
                        const baseCells = [
                            this.cells[rowA.row * this.size + sharedCol],
                            this.cells[rowB.row * this.size + sharedCol],
                        ];
                        return {
                            id: towerCellA.id,
                            answer: candidate,
                            strategy: "strategySkyscraper",
                            description: this.descriptionSkyscraper.bind(this),
                            data: {
                                candidates: [candidate],
                                baseCellIds: baseCells.map(cell => cell.id),
                                towerCellIds: [towerCellA.id, towerCellB.id],
                                eliminationCellIds: eliminationCells.map(cell => cell.id),
                                patternType: "row-based"
                            }
                        };
                    }
                }
            }

            const colsWithCandidate: Array<{ col: number; rows: number[]; cells: Cell[]; }> = [];
            for (let c = 0; c < this.size; c++) {
                const column = this.getColumns()[c];
                const candidateCells = column.filter(cell =>
                    !cell.guess && cell.solverPossibilites.has(candidate)
                );
                if (candidateCells.length === 2) {
                    colsWithCandidate.push({
                        col: c,
                        rows: [candidateCells[0].row, candidateCells[1].row],
                        cells: candidateCells
                    });
                }
            }

            for (let i = 0; i < colsWithCandidate.length - 1; i++) {
                for (let j = i + 1; j < colsWithCandidate.length; j++) {
                    const colA = colsWithCandidate[i];
                    const colB = colsWithCandidate[j];
                    const sharedRows = colA.rows.filter(row => colB.rows.includes(row));
                    if (sharedRows.length !== 1) {
                        continue;
                    }
                    const sharedRow = sharedRows[0];
                    const towerRowA = colA.rows.find(row => row !== sharedRow);
                    const towerRowB = colB.rows.find(row => row !== sharedRow);
                    if (towerRowA === undefined || towerRowB === undefined || towerRowA === towerRowB) {
                        continue;
                    }

                    const towerCellA = colA.cells.find(cell => cell.row === towerRowA);
                    const towerCellB = colB.cells.find(cell => cell.row === towerRowB);
                    if (!towerCellA || !towerCellB) {
                        continue;
                    }

                    const peersA = this.getAllRelatedCells(towerCellA.id, true);
                    const peersB = this.getAllRelatedCells(towerCellB.id, true);
                    const peerSetA = new Set(peersA.map(cell => cell.id));
                    const eliminationCells = peersB.filter(cell =>
                        peerSetA.has(cell.id) &&
                        !cell.guess &&
                        cell.solverPossibilites.has(candidate)
                    );

                    if (eliminationCells.length > 0) {
                        const baseCells = [
                            this.cells[sharedRow * this.size + colA.col],
                            this.cells[sharedRow * this.size + colB.col],
                        ];
                        return {
                            id: towerCellA.id,
                            answer: candidate,
                            strategy: "strategySkyscraper",
                            description: this.descriptionSkyscraper.bind(this),
                            data: {
                                candidates: [candidate],
                                baseCellIds: baseCells.map(cell => cell.id),
                                towerCellIds: [towerCellA.id, towerCellB.id],
                                eliminationCellIds: eliminationCells.map(cell => cell.id),
                                patternType: "column-based"
                            }
                        };
                    }
                }
            }
        }
        return null;
    }

    /**
     * Two-String Kite - row and column with two candidates linked by a box
     */
    strategyTwoStringKite(): Hint | null {
        for (const candidate of this.validInputs) {
            const rowsWithCandidate: Array<{ row: number; cells: Cell[] }> = [];
            for (let r = 0; r < this.size; r++) {
                const row = this.getRows()[r];
                const candidateCells = row.filter(cell =>
                    !cell.guess && cell.solverPossibilites.has(candidate)
                );
                if (candidateCells.length === 2) {
                    rowsWithCandidate.push({ row: r, cells: candidateCells });
                }
            }

            const colsWithCandidate: Array<{ col: number; cells: Cell[] }> = [];
            for (let c = 0; c < this.size; c++) {
                const column = this.getColumns()[c];
                const candidateCells = column.filter(cell =>
                    !cell.guess && cell.solverPossibilites.has(candidate)
                );
                if (candidateCells.length === 2) {
                    colsWithCandidate.push({ col: c, cells: candidateCells });
                }
            }

            for (const rowEntry of rowsWithCandidate) {
                for (const colEntry of colsWithCandidate) {
                    for (const rowCell of rowEntry.cells) {
                        for (const colCell of colEntry.cells) {
                            if (rowCell.id === colCell.id) {
                                continue;
                            }
                            if (rowCell.box !== colCell.box) {
                                continue;
                            }

                            const rowOther = rowEntry.cells.find(cell => cell.id !== rowCell.id);
                            const colOther = colEntry.cells.find(cell => cell.id !== colCell.id);
                            if (!rowOther || !colOther) {
                                continue;
                            }

                            if (rowOther.box === colOther.box) {
                                continue;
                            }

                            const peersA = this.getAllRelatedCells(rowOther.id, true);
                            const peerSetA = new Set(peersA.map(cell => cell.id));
                            const eliminationCells = this.getAllRelatedCells(colOther.id, true).filter(cell =>
                                peerSetA.has(cell.id) &&
                                !cell.guess &&
                                cell.solverPossibilites.has(candidate) &&
                                cell.id !== rowCell.id &&
                                cell.id !== colCell.id &&
                                cell.id !== rowOther.id &&
                                cell.id !== colOther.id
                            );

                            if (eliminationCells.length > 0) {
                                return {
                                    id: rowOther.id,
                                    answer: candidate,
                                    strategy: "strategyTwoStringKite",
                                    description: this.descriptionTwoStringKite.bind(this),
                                    data: {
                                        rowCellIds: rowEntry.cells.map(cell => cell.id),
                                        colCellIds: colEntry.cells.map(cell => cell.id),
                                        linkCellIds: [rowCell.id, colCell.id],
                                        endCellIds: [rowOther.id, colOther.id],
                                        eliminationCellIds: eliminationCells.map(cell => cell.id)
                                    }
                                };
                            }
                        }
                    }
                }
            }
        }
        return null;
    }

    /**
     * W-Wing - matching non-peer bi-value cells connected by a strong link
     */
    strategyWWing(): Hint | null {
        const units: Array<{ type: SudokuUnitType; index: number; cells: Cell[] }> = [
            ...this.getRows().map((cells, index) => ({ type: "row", index, cells })),
            ...this.getColumns().map((cells, index) => ({ type: "column", index, cells })),
            ...this.getBoxes().map((cells, index) => ({ type: "box", index, cells }))
        ];
        const matchingWingGroups = new Map<string, { candidates: string[]; cells: Cell[] }>();
        this.cells.forEach(cell => {
            if (cell.guess || cell.solverPossibilites.size !== 2) {
                return;
            }
            const candidates = Array.from(cell.solverPossibilites).sort();
            const key = candidates.join("|");
            const group = matchingWingGroups.get(key) ?? { candidates, cells: [] };
            group.cells.push(cell);
            matchingWingGroups.set(key, group);
        });

        const strongLinksByCandidate = new Map<string, Array<{
            unitType: SudokuUnitType;
            unitIndex: number;
            cells: [Cell, Cell];
        }>>();
        for (const candidate of this.validInputs) {
            for (const unit of units) {
                const candidateCells = unit.cells.filter(cell =>
                    !cell.guess && cell.solverPossibilites.has(candidate)
                );
                if (candidateCells.length !== 2) {
                    continue;
                }
                const links = strongLinksByCandidate.get(candidate) ?? [];
                links.push({
                    unitType: unit.type,
                    unitIndex: unit.index,
                    cells: [candidateCells[0], candidateCells[1]]
                });
                strongLinksByCandidate.set(candidate, links);
            }
        }

        for (const { candidates, cells: possibleWings } of matchingWingGroups.values()) {
            for (let i = 0; i < possibleWings.length - 1; i++) {
                const firstWing = possibleWings[i];
                const firstWingPeers = this.getRelatedCellIdSet(firstWing.id);

                for (let j = i + 1; j < possibleWings.length; j++) {
                    const secondWing = possibleWings[j];

                    if (firstWingPeers.has(secondWing.id)) {
                        continue;
                    }

                    const secondWingPeers = this.getRelatedCellIdSet(secondWing.id);

                    for (const strongLinkCandidate of candidates) {
                        const eliminationCandidate = candidates.find(candidate =>
                            candidate !== strongLinkCandidate
                        );
                        if (!eliminationCandidate) {
                            continue;
                        }

                        const strongLinks = strongLinksByCandidate.get(strongLinkCandidate) ?? [];
                        if (strongLinks.length === 0) {
                            continue;
                        }

                        const wingCellIds = new Set([
                            firstWing.id,
                            secondWing.id
                        ]);
                        const eliminationCells = this.cells.filter(cell =>
                            !cell.guess &&
                            !wingCellIds.has(cell.id) &&
                            cell.solverPossibilites.has(eliminationCandidate) &&
                            firstWingPeers.has(cell.id) &&
                            secondWingPeers.has(cell.id)
                        );
                        if (eliminationCells.length === 0) {
                            continue;
                        }

                        for (const strongLink of strongLinks) {
                            if (strongLink.cells.some(cell =>
                                cell.id === firstWing.id || cell.id === secondWing.id
                            )) {
                                continue;
                            }

                            const orientations = [
                                strongLink.cells,
                                [strongLink.cells[1], strongLink.cells[0]]
                            ];
                            for (const [firstLink, secondLink] of orientations) {
                                if (
                                    !firstWingPeers.has(firstLink.id) ||
                                    !secondWingPeers.has(secondLink.id)
                                ) {
                                    continue;
                                }

                                return {
                                    id: firstWing.id,
                                    answer: eliminationCandidate,
                                    strategy: "strategyWWing",
                                    description: this.descriptionWWing.bind(this),
                                    data: {
                                        wingCellIds: [firstWing.id, secondWing.id],
                                        candidates,
                                        strongLinkCellIds: [firstLink.id, secondLink.id],
                                        strongLinkCandidate,
                                        eliminationCandidate,
                                        eliminationCellIds: eliminationCells.map(cell => cell.id),
                                        unitType: strongLink.unitType,
                                        unitIndex: strongLink.unitIndex
                                    }
                                };
                            }
                        }
                    }
                }
            }
        }
        return null;
    }

    /**
     * XY-Wing - A pattern with a pivot cell and two pincers that form eliminations
     */
    strategyXYWing(): Hint | null {
        // Find all cells with exactly 2 candidates (potential pivot and pincers)
        const biValueCells = this.cells.filter(cell =>
            !cell.guess && cell.solverPossibilites.size === 2
        );

        for (const pivot of biValueCells) {
            const pivotCandidates = Array.from(pivot.solverPossibilites);
            const [x, y] = pivotCandidates;

            // Look for pincers: cells that share a unit with pivot and have one candidate in common
            const relatedCells = this.getAllRelatedCells(pivot.id);
            const potentialPincers = relatedCells.filter(cell =>
                !cell.guess && cell.solverPossibilites.size === 2 && cell !== pivot
            );

            for (let i = 0; i < potentialPincers.length - 1; i++) {
                for (let j = i + 1; j < potentialPincers.length; j++) {
                    const pincer1 = potentialPincers[i];
                    const pincer2 = potentialPincers[j];

                    const pincer1Candidates = Array.from(pincer1.solverPossibilites);
                    const pincer2Candidates = Array.from(pincer2.solverPossibilites);

                    // Check for XY-Wing pattern:
                    // Pivot: XY, Pincer1: XZ, Pincer2: YZ
                    let z: string | null = null;

                    // Check if pincer1 has X and some Z, pincer2 has Y and same Z
                    if (pincer1Candidates.includes(x) && pincer2Candidates.includes(y)) {
                        const z1 = pincer1Candidates.find(c => c !== x);
                        const z2 = pincer2Candidates.find(c => c !== y);
                        if (z1 === z2) {
                            z = z1!;
                        }
                    }
                    // Check if pincer1 has Y and some Z, pincer2 has X and same Z
                    else if (pincer1Candidates.includes(y) && pincer2Candidates.includes(x)) {
                        const z1 = pincer1Candidates.find(c => c !== y);
                        const z2 = pincer2Candidates.find(c => c !== x);
                        if (z1 === z2) {
                            z = z1!;
                        }
                    }

                    if (z) {
                        // Find elimination targets: cells that can see both pincers and contain Z
                        const pincer1Related = this.getAllRelatedCells(pincer1.id);
                        const pincer2Related = this.getAllRelatedCells(pincer2.id);

                        const eliminationCells = this.cells.filter(cell =>
                            !cell.guess && cell.solverPossibilites.has(z) &&
                            pincer1Related.includes(cell) && pincer2Related.includes(cell) &&
                            cell !== pincer1 && cell !== pincer2 && cell !== pivot
                        );

                        if (eliminationCells.length > 0) {
                            return {
                                id: pivot.id,
                                answer: z,
                                strategy: "strategyXYWing",
                                description: this.descriptionXYWing.bind(this),
                                data: {
                                    pivotCellId: pivot.id,
                                    pincerCellIds: [pincer1.id, pincer2.id],
                                    pivotCandidates: pivotCandidates,
                                    eliminationCandidate: z,
                                    eliminationCellIds: eliminationCells.map(cell => cell.id)
                                }
                            };
                        }
                    }
                }
            }
        }
        return null;
    }

    /**
     * XYZ-Wing - An extension of XY-Wing where the pivot cell has 3 candidates
     */
    strategyXYZWing(): Hint | null {
        // Find all cells with exactly 3 candidates (potential pivots)
        const triValueCells = this.cells.filter(cell =>
            !cell.guess && cell.solverPossibilites.size === 3
        );

        // Find all cells with exactly 2 candidates (potential pincers)
        const biValueCells = this.cells.filter(cell =>
            !cell.guess && cell.solverPossibilites.size === 2
        );

        for (const pivot of triValueCells) {
            const pivotCandidates = Array.from(pivot.solverPossibilites);
            const pivotRelated = this.getAllRelatedCells(pivot.id);

            // Find potential pincers that the pivot can see
            const potentialPincers = biValueCells.filter(cell =>
                pivotRelated.includes(cell)
            );

            // Check all combinations of two pincers
            for (let i = 0; i < potentialPincers.length - 1; i++) {
                for (let j = i + 1; j < potentialPincers.length; j++) {
                    const pincer1 = potentialPincers[i];
                    const pincer2 = potentialPincers[j];

                    const pincer1Candidates = Array.from(pincer1.solverPossibilites);
                    const pincer2Candidates = Array.from(pincer2.solverPossibilites);

                    // All three cells combined must have exactly 3 candidates
                    const allCandidates = new Set([
                        ...pivotCandidates,
                        ...pincer1Candidates,
                        ...pincer2Candidates
                    ]);

                    if (allCandidates.size !== 3) {
                        continue;
                    }

                    // The pincers must have exactly one candidate in common
                    const commonCandidates = pincer1Candidates.filter(c =>
                        pincer2Candidates.includes(c)
                    );

                    if (commonCandidates.length !== 1) {
                        continue;
                    }

                    const eliminationCandidate = commonCandidates[0];

                    // Find elimination targets: cells that can see both pincers AND the pivot
                    const pincer1Related = this.getAllRelatedCells(pincer1.id);
                    const pincer2Related = this.getAllRelatedCells(pincer2.id);

                    const eliminationCells = this.cells.filter(cell =>
                        !cell.guess &&
                        cell.solverPossibilites.has(eliminationCandidate) &&
                        pivotRelated.includes(cell) &&
                        pincer1Related.includes(cell) &&
                        pincer2Related.includes(cell) &&
                        cell !== pivot && cell !== pincer1 && cell !== pincer2
                    );

                    if (eliminationCells.length > 0) {
                        return {
                            id: pivot.id,
                            answer: eliminationCandidate,
                            strategy: "strategyXYZWing",
                            description: this.descriptionXYZWing.bind(this),
                            data: {
                                pivotCellId: pivot.id,
                                pincerCellIds: [pincer1.id, pincer2.id],
                                pivotCandidates,
                                pincerCandidates: [pincer1Candidates, pincer2Candidates],
                                eliminationCandidate,
                                eliminationCellIds: eliminationCells.map(c => c.id)
                            }
                        };
                    }
                }
            }
        }
        return null;
    }

    /**
     * Helper function to check if two sets are equal
     */
    private setsEqual<T>(set1: Set<T>, set2: Set<T>): boolean {
        if (set1.size !== set2.size) return false;
        for (const item of set1) {
            if (!set2.has(item)) return false;
        }
        return true;
    }

    /**
     * Helper function to check if set1 is a subset of set2
     */
    private isSetSubset<T>(set1: Set<T>, set2: Set<T>): boolean {
        for (const item of set1) {
            if (!set2.has(item)) return false;
        }
        return true;
    }
}

window.SudokuGame = SudokuGame;

var AllPossibilities = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F', 'G']

interface GetCellColorsParams  {
    activeCellId: number;
    highlightActiveCell: boolean;
    highlightActiveNumber: string;
    activeNumber: string;
    showIncorrectMoves: boolean;
    relatedCellIds?: Set<number> | null;
}

class Cell {
    /**
     * The unique id of the cell from 0 to (size * size) - 1
     * @type {number}
     */
    id: number;

    /**
     * The 0-index row the cell is in
     * @type {number}
     */
    row: number;

    /**
     * The 0-index column the cell is in
     * @type {number}
     */
    column: number;

    /**
     * The 0-index box the cell is in
     * @type {number}
     */
    box: number;

    /**
     * The user defined possibilities
     * @type {Set<string>}
     */
    possibilities: Set<string> = new Set();

    /**
     * possibilities used for the solver to determine a hint or to solve the puzzle
     */
    solverPossibilites: Set<string> = new Set();

    /**
     * The users inputted answer.
     * @type {string}
     */
    guess: string | null = null;

    /**
     * The correct answer for this cell
     * @type {string}
     */
    answer: string;

    /**
     * Is this cell a given cell for the puzzle
     * @type {boolean}
     */
    isGiven: boolean = false;

    /**
     * Set to true if this cell is marked as a hint
     * @type {boolean}
     */
    isHint: boolean = false;

    /**
     * Set to true if this cell is marked as a hint
     * @type {boolean}
     */
    isDescription: boolean = false;

    /**
     * Set to true if this cell is marked as an indication (bright yellow highlight)
     * @type {boolean}
     */
    isIndication: boolean = false;

    /**
     * Indicates whether the cell needs to be re-rendered.
     * @type {boolean}
     */
    isDirty: boolean = true;

    constructor() {
    }

    markDirty() {
        this.isDirty = true;
    }

    markHint() {
        this.isDirty = true;
        this.isHint = true;
    }

    clearHint() {
        this.isHint = false;
        this.isDirty = true;
    }

    clearDirty() {
        this.isDirty = false;
    }

    /**
     * Clear the guess and possibilities.
     * @returns
     */
    clear() {
        if (this.isGiven) {
            return;
        }
        this.guess = null;
        this.possibilities.clear();
        this.markDirty();
    }
    /**
     * Clear all possibilities
     */
    resetPossibilities() {
        this.possibilities.clear();
        this.markDirty();
    }

    /**
     * The guess is correct for this cell
     * @returns
     */
    isCorrect() {
        return this.guess && this.guess == this.answer
    }
    /**
     * There is a guess for this cell and it does not match the answer
     * @returns
     */
    isWrong() {
        return this.guess && this.guess != this.answer
    }

    /**
     * Set the guess for the cell
     * @param {string} guess
     * @returns true if the guess is correct, false otherwise
     */
    addGuess(guess) {
        if (guess == ""){
            return;
        }
        guess = guess.toString()

        this.guess = guess;
        this.markDirty();
        return this.guess == this.answer;
    }

    /**
     * Returns true if this cell as user specified possibilities
     * @returns
     */
    hasPossibilities() {
        return this.possibilities.size > 0;
    }
    /**
     * Adds the possibility or removes it if it is already in the set.
     * @param {string} possibility
     */
    togglePossibility(possibility) {
        possibility = possibility.toString()
        if (this.possibilities.has(possibility)) {
            this.possibilities.delete(possibility)
        } else {
            this.possibilities.add(possibility)
        }
        this.markDirty();
    }
    /**
     * Removes the possibility
     * @param {string} possibility
     */
    removePossibility(possibility) {
        const removed = this.possibilities.delete(possibility.toString())
        if (removed) {
            this.markDirty();
        }
    }

    replacePossibilitiesWithSolverPossibilities() {
        // clone the solver possibilities into the new set, so they don't
        // end up sharing a reference
        this.possibilities = new Set(this.solverPossibilites);
        this.markDirty();
    }

    /**
     * Converts the possibilites to an array and sorts for easier reading
     * @returns
     */
    getPossibilitiesArray() {
        return Array.from(this.possibilities).sort();
    }

    /**
     * Converts the cell to a string
     * @returns
     */
    serialize() {
        if (this.guess) {
            return this.guess;
        } else if (this.possibilities.size > 0) {
            return 'P' + Array.from(this.possibilities).join('')
        }
        return ' '
    }
    /**
     * Renders the cell
     * @param {{possibilitiesInGrid: boolean,showIncorrectMoves: boolean, showSolution:boolean }} param0
     * @returns
     */
    render({ activeCellId, highlightActiveCell, possibilitiesInGrid, showIncorrectMoves, showSolution, gridBoxWidth, gridBoxHeight, highlightActiveNumber, activeNumber, cageData, relatedCellIds }: CellRenderOptions) {
        if (highlightActiveNumber === "false") {
            activeNumber = null;
        }

        if (this.isGiven) {
            return this.renderGiven({ highlightActiveNumber, activeNumber, activeCellId, highlightActiveCell, cageData, relatedCellIds });
        } else if (showSolution) {
            return this.renderAnswer({ cageData });
        } else if (this.guess) {
            return this.renderGuess({ showIncorrectMoves, activeNumber, activeCellId, highlightActiveCell, highlightActiveNumber, cageData, relatedCellIds });
        } else {
            return this.renderPossibilities({ possibilitiesInGrid, gridBoxWidth, gridBoxHeight, activeNumber, activeCellId, highlightActiveCell, highlightActiveNumber, cageData, relatedCellIds });
        }
    }

    renderGiven({ highlightActiveNumber, activeNumber, activeCellId, highlightActiveCell, cageData, relatedCellIds }: { highlightActiveNumber: "false" | "true" | "highlight"; activeNumber: string | null; activeCellId: number | null; highlightActiveCell: boolean; cageData?: CageRenderData; relatedCellIds?: Set<number> | null; }) {
        const isActiveNumber = this.answer == activeNumber;
        const isActiveCell = this.id == activeCellId && highlightActiveCell;
        let numberStyle = "font-weight:700;";
        if (highlightActiveNumber !== "false") {
            if (isActiveNumber) {
                numberStyle = "font-weight:900;";
            } else {
                numberStyle = "font-weight:400;";
            }
        }

        let color = this.getCellColors({ activeCellId, highlightActiveCell, relatedCellIds });
        if (highlightActiveNumber === "highlight" && !isActiveCell) {
            color += isActiveNumber ? " background-color: #fff59b;" : "";
        }
        const contentStyle = cageData ? "" : color;

        const content = `
        <div style="display: flex;
            justify-content: center;
            align-items: center;
            width:100%; height:100%;
            ${contentStyle}">
                <div style="margin: auto;  ${numberStyle} text-align: center;">${this.guess}</div>
            </div>`;
        return this.wrapWithCage(content, cageData, color);
    }

    renderAnswer({ cageData }: { cageData?: CageRenderData }) {
        const content = `
        <div style="display: flex;
            justify-content: center;
            align-items: center;
            width:100%; height:100%;>
                <div style="margin: auto; text-align: center;">${this.answer}</div>
            </div>`;
        return this.wrapWithCage(content, cageData);
    }

    renderGuess({ showIncorrectMoves, activeNumber, activeCellId, highlightActiveCell, highlightActiveNumber, cageData, relatedCellIds }: { showIncorrectMoves: boolean; activeNumber: string | null; activeCellId: number | null; highlightActiveCell: boolean; highlightActiveNumber: "false" | "true" | "highlight"; cageData?: CageRenderData; relatedCellIds?: Set<number> | null; }) {
        const isActiveNumber = this.guess == activeNumber;
        const isActiveCell = this.id == activeCellId && highlightActiveCell;
        const isIncorrectMove = showIncorrectMoves && this.guess && this.guess != this.answer;
        let activeNumberStyle = isActiveNumber && highlightActiveNumber === "true" ? "font-weight:900;" : "";
        let style = this.getCellColors({ activeCellId, highlightActiveCell, showIncorrectMoves, highlightActiveNumber, activeNumber, relatedCellIds });
        const contentStyle = cageData ? "" : style;

        const content = `
        <div style="display: flex;
            justify-content: center;
            align-items: center;
            width:100%; height:100%;
            ${contentStyle}">
                <div style="margin: auto; ${activeNumberStyle} text-align: center;">${this.guess}</div>
            </div>`;
        return this.wrapWithCage(content, cageData, style);
    }

    renderHint() {
        return `
        <div style="display: flex;
            justify-content: center;
            align-items: center;
            width:100%; height:100%;
            background-color: #00ccff;"">
            </div>`
    }

    renderPossibilities({ possibilitiesInGrid, gridBoxHeight, gridBoxWidth, activeNumber, activeCellId, highlightActiveCell, highlightActiveNumber, cageData, relatedCellIds }: { possibilitiesInGrid: boolean; gridBoxHeight: number; gridBoxWidth: number; activeNumber: string | null; activeCellId: number | null; highlightActiveCell: boolean; highlightActiveNumber: "false" | "true" | "highlight"; cageData?: CageRenderData; relatedCellIds?: Set<number> | null; }) {
        const cellStyle = this.getCellColors({ activeCellId, highlightActiveCell, relatedCellIds });
        const contentStyle = cageData ? "" : cellStyle;
        const content = possibilitiesInGrid
            ? this.renderGridPossibilities({ gridBoxHeight, gridBoxWidth, activeNumber, highlightActiveNumber, cellStyle: contentStyle })
            : this.renderListPossibilities({ activeNumber, highlightActiveNumber, cellStyle: contentStyle });
        return this.wrapWithCage(content, cageData, cellStyle);
    }

    renderListPossibilities({ activeNumber, highlightActiveNumber, cellStyle }: { activeNumber: string | null; highlightActiveNumber: "false" | "true" | "highlight"; cellStyle: string; }) {
        const possibilities = this.getPossibilitiesArray().map((val) => {
            const isActiveNumber = val == activeNumber;
            let activeNumberStyle = isActiveNumber ? "font-weight:900;" : "";
            activeNumberStyle += highlightActiveNumber === "highlight" && isActiveNumber ? " background-color: #fff59b;" : "";
            return `<span style="${activeNumberStyle}">${val}</span>`
        }).join("&nbsp")
        return `<div class="POSSDIV" style="height:100%; width:100%; ${cellStyle}; overflow-wrap:anywhere; text-align: right;">${possibilities}&nbsp</div>`
    }

    getPossibilitityText(number, activeNumber, highlightActiveNumber: "false" | "true" | "highlight") {
        number = number.toString()
        if (!this.possibilities.has(number)) {
            return '&nbsp;'
        }
        const isActiveNumber = number == activeNumber;
        let activeNumberStyle = isActiveNumber ? "font-weight:900;" : "";
        if (highlightActiveNumber === "highlight" && isActiveNumber) {
            activeNumberStyle += " background-color: #fff59b;";
        }
        return `<span style="${activeNumberStyle}">${number}</span>`
    }

    renderGridPossibilities({ gridBoxHeight, gridBoxWidth, activeNumber, highlightActiveNumber, cellStyle }: { gridBoxHeight: number; gridBoxWidth: number; activeNumber: string | null; highlightActiveNumber: "false" | "true" | "highlight"; cellStyle: string; }) {

        const tableRows: string[] = [];
        const tdWidth = Math.floor(100 / gridBoxWidth);
        for (var i = 0; i < gridBoxHeight; i++) {
            const row: string[] = [];
            for (var j = 0; j < gridBoxWidth; j++) {
                const val = AllPossibilities[i * gridBoxWidth + j];
                let candidateStyle = ""
                if (val == activeNumber && highlightActiveNumber === "highlight" && this.possibilities.has(val)) {
                    candidateStyle +=" background-color: #fff59b;";
                }
                const td = ` <td align="center" style="${candidateStyle}" width="${tdWidth}%">${this.getPossibilitityText(val, activeNumber, highlightActiveNumber)}</td>`
                row.push(td);
            }
            tableRows.push(`<tr>${row.join("")}</tr>`)
        }

        return `
            <table style="${cellStyle}" class="posstable" cellspacing="0" cellpadding="0">
                <tbody>
                  ${tableRows.join("")}
                </tbody>
              </table>
        `
    }

    private wrapWithCage(content: string, cageData?: CageRenderData, cellStyle: string = "") {
        if (!cageData) {
            return content;
        }

        const corners = cageData.corners || { topLeft: false, topRight: false, bottomRight: false, bottomLeft: false };

        const borders: string[] = [];
        if (cageData.borders.top) {
            const topClasses = ["killer-cage-border", "killer-border-top"];
            if (!cageData.borders.left) {
                topClasses.push("killer-border-top-full-left");
            }
            if (!cageData.borders.right) {
                topClasses.push("killer-border-top-full-right");
            }
            borders.push(`<div class="${topClasses.join(" ")}"></div>`);
        }
        if (cageData.borders.right) {
            const rightClasses = ["killer-cage-border", "killer-border-right"];
            if (!cageData.borders.top) {
                rightClasses.push("killer-border-right-full-top");
            }
            if (!cageData.borders.bottom) {
                rightClasses.push("killer-border-right-full-bottom");
            }
            borders.push(`<div class="${rightClasses.join(" ")}"></div>`);
        }
        if (cageData.borders.bottom) {
            const bottomClasses = ["killer-cage-border", "killer-border-bottom"];
            if (!cageData.borders.left) {
                bottomClasses.push("killer-border-bottom-full-left");
            }
            if (!cageData.borders.right) {
                bottomClasses.push("killer-border-bottom-full-right");
            }
            borders.push(`<div class="${bottomClasses.join(" ")}"></div>`);
        }
        if (cageData.borders.left) {
            const leftClasses = ["killer-cage-border", "killer-border-left"];
            if (!cageData.borders.top) {
                leftClasses.push("killer-border-left-full-top");
            }
            if (!cageData.borders.bottom) {
                leftClasses.push("killer-border-left-full-bottom");
            }
            borders.push(`<div class="${leftClasses.join(" ")}"></div>`);
        }

        // Corner joiners to make dashed edges meet cleanly
        if (corners.topLeft) {
            borders.push('<div class="killer-corner killer-corner-top-left"></div>');
        }
        if (corners.topRight) {
            borders.push('<div class="killer-corner killer-corner-top-right"></div>');
        }
        if (corners.bottomRight) {
            borders.push('<div class="killer-corner killer-corner-bottom-right"></div>');
        }
        if (corners.bottomLeft) {
            borders.push('<div class="killer-corner killer-corner-bottom-left"></div>');
        }

        const wrapperClasses = ["killer-cell-wrapper"];
        if (cageData.isOrigin) {
            wrapperClasses.push("killer-cell-has-sum");
        }
        if (cageData.isHighlighted) {
            wrapperClasses.push("killer-cage-highlighted");
        }
        const sumMarkup = cageData.isOrigin ? `<div class="killer-cage-sum">${cageData.sum}</div>` : '';
        const wrapperStyle = cellStyle ? ` style="${cellStyle}"` : "";

        return `<div class="${wrapperClasses.join(' ')}"${wrapperStyle}>${sumMarkup}${borders.join('')}<div class="killer-cell-content">${content}</div></div>`;
    }

    getCellColors({ activeCellId, highlightActiveCell, highlightActiveNumber, activeNumber, showIncorrectMoves = false, relatedCellIds }: GetCellColorsParams) {
        const isActiveCell = this.id == activeCellId && highlightActiveCell;
        const guessIsCurrentActiveNumber = this.guess === activeNumber;
        const isIncorrectMove = showIncorrectMoves && this.guess && this.guess != this.answer;
        if (isIncorrectMove) {
            return isActiveCell ? "color:red; background-color:#f9b4c0; box-shadow: inset 0 0 0 1px #8e95a0;" : "color:red; background-color:#f7cfd6;";
        } else if (this.isHint) {
            return isActiveCell ? "background-color: #00ccff; box-shadow: inset 0 0 0 1px #8e95a0;" : "background-color: #00e5ff;";
        } else if (this.isIndication) {
            return "background-color: #ffff00;";
        } else if (this.isDescription) {
            return "background-color: #dce8f4;";
        } else if (highlightActiveNumber === "highlight" && !isActiveCell && guessIsCurrentActiveNumber) {
            return " background-color: #fff59b;"
        } else if (this.isGiven) {
            if (isActiveCell) {
                return "background-color:#b3d4e8;";
            }
            if (relatedCellIds && relatedCellIds.has(this.id)) {
                return "background-color:#c4d5e6;";
            }
            return "background-color:#eaeef2;";
        }
        if (isActiveCell) {
            return "background-color:#d0f5d5; box-shadow: inset 0 0 0 1px #8e95a0;";
        }
        // Related cell highlight (lowest priority)
        if (relatedCellIds && relatedCellIds.has(this.id)) {
            return "background-color:#d6e5f4;";
        }
        return "";
    }
}
export type CageDefinition = {
    id: number;
    cells: number[];
    sum: number;
    originCell: number;
};

type CageRenderData = {
    sum: number;
    borders: { top: boolean; right: boolean; bottom: boolean; left: boolean; };
    corners: { topLeft: boolean; topRight: boolean; bottomRight: boolean; bottomLeft: boolean; };
    isOrigin: boolean;
    isHighlighted: boolean;
};

type CellRenderOptions = {
    activeCellId: number | null;
    highlightActiveCell: boolean;
    possibilitiesInGrid: boolean;
    showIncorrectMoves: boolean;
    showSolution: boolean;
    gridBoxWidth: number;
    gridBoxHeight: number;
    highlightActiveNumber: "false" | "true" | "highlight";
    activeNumber: string | null;
    cageData?: CageRenderData;
    relatedCellIds: Set<number> | null;
};

type RenderStateSnapshot = {
    possibilitiesInGrid: boolean;
    showIncorrectMoves: boolean;
    showSolution: boolean;
    highlightActiveNumber: "false" | "true" | "highlight";
    activeNumber: string | null;
    highlightActiveCell: boolean;
    activeCellId: number | null;
    relatedCellIds: Set<number> | null;
};
